// DEPENDENCIES
import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./RFP.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall, putAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";
import { unsavedChangesGuardRef } from "../../../context/unsavedChangesGuard";

// MUI COMPONENTS
import {
	Button,
	ButtonGroup,
	TextField,
	Autocomplete,
	Checkbox,
	FormGroup,
	FormControlLabel,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
	MenuItem,
	Radio,
	RadioGroup,
	Collapse,
	Alert,
	AlertTitle,
	CircularProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Dialog,
	DialogTitle,
	DialogContent,
	Chip,
	Typography,
	useMediaQuery,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// CUSTOM COMPONENTS
import J2MultilineTextField from "../../Misc/J2MultilineTextField/J2MultilineTextField";
import J2ErrorDialog from "../../Misc/J2ErrorDialog/J2ErrorDialog";

// Help Dialogs
import ClientInformationHelpDialog from "./RFPHelpDialogs/ClientInformationHelpDialog/ClientInformationHelpDialog";
import PropertyInformationHelpDialog from "./RFPHelpDialogs/PropertyInformationHelpDialog/PropertyInformationHelpDialog";
import ProposalSummaryHelpDialog from "./RFPHelpDialogs/ProposalSummaryHelpDialog/ProposalSummaryHelpDialog";

// Drafts
import RFPDraftsDrawer from "./RFPDraftsDrawer";
import { getCurrentTimeUTC } from "../../../constants/dateTime";

// CONSTANTS
const searchByOptionsMap = new Map([
	["email", "Email"],
	["lastname", "Last Name"],
]);

const rfpSourceMap = {
	referral: "Referral",
	returnClient: "Return Client",
	internetSearch: "Internet Search",
	socialMedia: "Social Media",
	j2Website: "J2 Website",
	j2EducationEvent: "J2 Education Event",
	j2SocialEvent: "J2 Social Event",
	externalEvent: "External Event (CAI, OWCAM, etc.)",
	clientDrop: "Client Drop (Holiday Gift or Office Visit)",
	santaVisit: "Santa Visit",
	directMail: "Direct Mail Campaign",
	ad: "Ads",
};

const buildingTypes = {
	Apartment: "Apartment",
	Commercial: "Commercial Building/Mixed Use",
	Condominium: "Condominium HOA",
	Nonprofit: "Nonprofit",
	Public: "Public",
	School: "School",
	"Single Family Residence": "Single Family Residence",
	Other: "Other",
};

const formatZipCodeNumber = (value) => {
	if (!value) return "";

	return value.toString().replace(/\D/g, "").slice(0, 9);
};

// OTHER
// To convert the date to UTC for compatibility with HubSpot API
var utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

// REACT COMPONENT
const RFP = () => {
	// MSAL
	const { instance, accounts } = useMsal();

	// REFS
	const clientSearchByValueRef = useRef(null);
	const propertySearchByValueRef = useRef(null);

	// API RESULT STATES
	const [clientOptions, setClientOptions] = useState([]);
	const [propertyOptions, setPropertyOptions] = useState([]);

	// CREATE NEW/SEARCH EXISTING STATES
	const [createNewClientEnabled, setCreateNewClientEnabled] = useState(false);
	const [searchExistingClientEnabled, setSearchExistingClientEnabled] =
		useState(true);

	const [createNewPropertyEnabled, setCreateNewPropertyEnabled] =
		useState(false);
	const [searchExistingPropertyEnabled, setSearchExistingPropertyEnabled] =
		useState(true);

	// DETAILED VIEW/SEARCH BAR COLLAPSE STATES
	const [clientDetailedViewOpen, setClientDetailedViewOpen] = useState(false);
	const [clientSearchBarOpen, setClientSearchBarOpen] = useState(true);

	const [propertyDetailedViewOpen, setPropertyDetailedViewOpen] =
		useState(false);
	const [propertySearchBarOpen, setPropertySearchBarOpen] = useState(true);

	// SEARCH BAR STATES
	const [clientSearchByParameter, setClientSearchByParameter] =
		useState("lastname");
	const [clientSearchByValue, setClientSearchByValue] = useState("");

	const [clientSearchByValueKey, setClientSearchByValueKey] = useState(true); // key to refresh the Autocomplete TF

	const [propertySearchByValue, setPropertySearchByValue] = useState("");

	// DETAILED VIEW STATES
	const [clientInfo, setClientInfo] = useState({
		email: "",
		firstname: "",
		lastname: "",
		recordID: "",
	});

	const [propertyInfo, setPropertyInfo] = useState({
		name: "",
		address1: "",
		address2: "",
		city: "",
		state: "",
		zip: "",
		recordID: "",
	});
	const propertyRecordIDRef = useRef("");
	propertyRecordIDRef.current = propertyInfo.recordID;

	// SERVICE STATES (CHECKBOXES)
	const [serviceCheckboxes, setServiceCheckboxes] = useState({
		visual: false,
		invasive: false,
		pca: false,
		design: false,
		bidAssistance: false,
		contractAdministration: false,
		rapidVisual: false,
		claimSupport: false,
		feasibility: false,
		generalConsulting: false,
		engineeringStructural: false,
		drainage: false,
		additionalServices: false,
		windowTesting: false,
		romExercise: false,
	});

	const [
		serviceInvasiveNumberOfOpenings,
		setServiceInvasiveNumberOfOpenings,
	] = useState("");

	// SERVICE STATES (ENABLED/DISABLED)
	const [enabledServices, setEnabledServices] = useState({
		visual: true,
		invasive: true,
		pca: true,
		design: true,
		bidAssistance: true,
		contractAdministration: true,
		rapidVisual: true,
		claimSupport: true,
		feasibility: true,
		generalConsulting: true,
		engineeringStructural: true,
		drainage: true,
		additionalServices: true,
		windowTesting: true,
		romExercise: true,
	});

	// SERVICE COST BASIS STATES (RADIO BUTTONS)
	const [serviceCostBases, setServiceCostBases] = useState({
		visual: "flatFee",
		invasive: "flatFee",
		pca: "flatFee",
		design: "flatFee",
		bidAssistance: "timeAndCost",
		contractAdministration: "timeAndCost",
		rapidVisual: "flatFee",
		claimSupport: "timeAndCost",
		feasibility: "flatFee",
		generalConsulting: "timeAndCost",
		engineeringStructural: "timeAndCost",
		drainage: "timeAndCost",
		additionalServices: "timeAndCost",
		windowTesting: "flatFee",
		romExercise: "flatFee",
	});

	const [claimType, setClaimType] = useState("");
	const [claimCause, setClaimCause] = useState({
		fire: false,
		flood: false,
		waterDamage: false,
		structural: false,
		design: false,
		cd: false,
	});

	// SERVICE MESSAGE STATES
	const [serviceMessageOpen, setServiceMessageOpen] = useState(false);
	const [serviceMessageText, setServiceMessageText] = useState("");

	// COMPONENT STATES (COUNTS)
	const [numComponents, setNumComponents] = useState(0);

	// COMPONENT/LOCATION STATES (CHECKBOXES)
	const [componentCheckboxes, setComponentCheckboxes] = useState({
		BE: false,
		interiorUnit: false,
		interiorAttic: false,
		interiorCrawlSpace: false,
		roof: false,
		site: false,
		carportGarage: false,
		decks: false,
		windows: false,
		stairway: false,
		plumbing: false,
		HVAC: false,
		foundation: false,
		other: false,
	});

	// COMPONENT STATES (ENABLED/DISABLED)
	const [enabledComponents, setEnabledComponents] = useState({
		BE: true,
		interiorUnit: true,
		interiorAttic: true,
		interiorCrawlSpace: true,
		roof: true,
		site: true,
		carportGarage: true,
		decks: true,
		windows: true,
		stairway: true,
		plumbing: true,
		HVAC: true,
		foundation: true,
		other: true,
	});

	// COMPONENT MESSAGE STATES
	const [componentMessageOpen, setComponentMessageOpen] = useState(false);
	const [componentMessageText, setComponentMessageText] = useState("");

	// VARIOUS STATES
	const [proposalSource, setProposalSource] = useState("");
	const [proposalSourceReferral, setProposalSourceReferral] = useState("");

	const [scopeBackground, setScopeBackground] = useState("");
	// building description prefilled into Scope / Background from an earlier RFP ({ description, submittedTime, recordID })
	const [scopeBackgroundPrefill, setScopeBackgroundPrefill] = useState(null);
	// latest rendered values, read after the prefill request resolves
	const scopeBackgroundRef = useRef("");
	scopeBackgroundRef.current = scopeBackground;
	const scopeBackgroundPrefillRef = useRef(null);
	scopeBackgroundPrefillRef.current = scopeBackgroundPrefill;
	const [additionalNotes, setAdditionalNotes] = useState("");

	const [buildingType, setBuildingType] = useState("");
	const [oic, setOIC] = useState("");
	const [proposalDueDate, setProposalDueDate] = useState(
		dayjs().add(3, "day"),
	);

	// ERROR STATES
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const [clientEmailError, setClientEmailError] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [validationIssues, setValidationIssues] = useState([]);

	// SUCCESS STATES
	const [isSuccess, setIsSuccess] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");

	const [isSubmitting, setIsSubmitting] = useState(false);

	// HELP DIALOG STATES
	const [
		clientInformationHelpDialogOpen,
		setClientInformationHelpDialogOpen,
	] = useState(false);
	const [
		propertyInformationHelpDialogOpen,
		setPropertyInformationHelpDialogOpen,
	] = useState(false);
	const [proposalSummaryHelpDialogOpen, setProposalSummaryHelpDialogOpen] =
		useState(false);

	// DRAFT STATES
	// Phones (same breakpoint as App.js): no page header (the mobile top bar
	// shows the title) and no drafts
	const isMobile = useMediaQuery("(max-width: 767px)");
	const [draftsDrawerOpen, setDraftsDrawerOpen] = useState(false);
	const [currentDraftID, setCurrentDraftID] = useState(null);
	const [currentDraftName, setCurrentDraftName] = useState("");
	const [saveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false);
	const [draftNameInput, setDraftNameInput] = useState("");
	const [isSavingDraft, setIsSavingDraft] = useState(false);
	const [newRFPConfirmOpen, setNewRFPConfirmOpen] = useState(false);
	const [draftLoadVersion, setDraftLoadVersion] = useState(0);
	const lastSavedDraftSnapshotRef = useRef(null);
	const pristineFormSnapshotRef = useRef(null);
	const isDraftDirtyRef = useRef(false);

	// LOGGING STATES
	const loggingPayload = useRef({
		submitClickedTime: "",
		submittedTime: "",
		errorMessage: "",
		userEmailRetrievedTime: "",
		clientHubspotCreatedFoundTime: "",
		propertyHubspotCreatedFoundTime: "",
		hubspotOwnerRetrievedTime: "",
		hubspotDealCreatedTime: "",
		hubspotAssociateContactDealTime: "",
		hubspotAssociatePropertyDealTime: "",
		hubspotTaskCreatedTime: "",
		hubspotAssociateTaskDealTime: "",
	});

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		if (employeeID !== 0) {
			postAPICall(instance, accounts[0], "/api/logs/access/add", {
				page_name: "RFP",
				employeeID: employeeID,
				date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
				notes: "Accessed the RFP page",
			});
		}
	}, []);

	// Browsers only allow a generic, non-customizable confirmation here (the
	// text set on returnValue is ignored by all modern browsers) - this is
	// the standard mechanism for "unsaved changes" warnings on tab close,
	// refresh, or navigating to a new URL.
	useEffect(() => {
		const handleBeforeUnload = (event) => {
			if (isDraftDirtyRef.current) {
				event.preventDefault();
				event.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () =>
			window.removeEventListener("beforeunload", handleBeforeUnload);
	}, []);

	// Covers in-app navigation (e.g. clicking another tool in the sidebar),
	// which beforeunload can't see since this app's BrowserRouter swaps
	// routes client-side with no real page unload. GuardedRouterLink (used
	// throughout Navigation.js) checks this on every link click.
	useEffect(() => {
		unsavedChangesGuardRef.current = () => isDraftDirtyRef.current;
		return () => {
			unsavedChangesGuardRef.current = null;
		};
	}, []);

	// Runs after mount, after loading a draft, and after "New RFP" resets the
	// form - each of those is a point where the form should read as "clean."
	// When a draft is loaded this baselines lastSavedDraftSnapshotRef; when
	// there's no loaded draft (a blank/reset form) it baselines
	// pristineFormSnapshotRef instead, so the dirty check below also catches
	// someone filling out a brand-new RFP that was never saved as a draft.
	useEffect(() => {
		const snapshot = JSON.stringify({
			draftName: currentDraftName,
			...buildDraftFormFields(),
		});
		if (currentDraftID) {
			lastSavedDraftSnapshotRef.current = snapshot;
		} else {
			pristineFormSnapshotRef.current = snapshot;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftLoadVersion]);

	useEffect(() => {
		if (numComponents >= 2) {
			disableUncheckedComponents();
			componentMessageOpened(
				"Only two components can be selected at a time.",
			);
		} else {
			enableAllComponents();
			componentMessageClosed();
		}
	}, [numComponents]);

	useEffect(() => {
		if (!serviceCheckboxes.claimSupport) {
			if (componentCheckboxes.other) {
				setComponentCheckboxes((prevInfo) => ({
					...prevInfo,
					other: false,
				}));
				setNumComponents((n) => (n > 0 ? n - 1 : 0));
			}
			if (claimType) {
				setClaimType("");
			}
			if (Object.values(claimCause).some(Boolean)) {
				setClaimCause({
					fire: false,
					flood: false,
					waterDamage: false,
					structural: false,
					design: false,
					cd: false,
				});
			}
		}
	}, [
		serviceCheckboxes.claimSupport,
		componentCheckboxes.other,
		claimType,
		claimCause,
	]);

	// CLIENT INPUT HANDLER METHODS
	const createNewClientClicked = () => {
		// if the create new client button is already enabled, don't do anything
		if (createNewClientEnabled) {
			return;
		}

		// close the client search bar
		setClientSearchBarOpen(false);

		// enable the create new client view
		setCreateNewClientEnabled(true);
		setSearchExistingClientEnabled(false);

		// clear all previous values that were in before
		setClientInfo({
			email: "",
			firstname: "",
			lastname: "",
			recordID: "",
		});

		// clear the searchbyvalue autocomplete
		const clientSearchByValueEle =
			clientSearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator",
			)[0];
		if (clientSearchByValueEle) clientSearchByValueEle.click();

		// open the client detailed view
		setClientDetailedViewOpen(true);
	};

	const searchExistingClientClicked = (override = false) => {
		// override is for when all the inputs are to be cleared, not just a repeated click on the search existing client button when already selected
		if (searchExistingClientEnabled && !override) {
			return;
		}

		// open the client search bar
		setClientSearchBarOpen(true);
		setClientDetailedViewOpen(false);

		// enable the search existing client view
		setSearchExistingClientEnabled(true);
		setCreateNewClientEnabled(false);

		// clear all previous values that were in before
		setClientSearchByValue("");

		setClientInfo({
			email: "",
			firstname: "",
			lastname: "",
			recordID: "",
		});
	};

	const clientSearchByParameterSelected = (event) => {
		const newParameter = event.target.value;
		setClientSearchByParameter(newParameter);

		// clear the searchByValue autocomplete
		const clientSearchByValueEle =
			clientSearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator",
			)[0];
		if (clientSearchByValueEle) clientSearchByValueEle.click();

		// close client detailed view
		setClientDetailedViewOpen(false);

		// clear all values that could've been in before
		setClientSearchByValue("");
		setClientSearchByValueKey(!clientSearchByValueKey); // this is a key to refresh the Autocomplete TF

		setClientInfo({
			email: "",
			firstname: "",
			lastname: "",
			recordID: "",
		});

		setClientOptions([]);
	};

	const clientSearchByValueSelected = (event, newValue) => {
		if (newValue && newValue.properties) {
			// if the searchByValue is selected and the user didn't just clear the autocomplete
			setClientDetailedViewOpen(true);

			setClientInfo({
				email: newValue.properties.email,
				firstname: newValue.properties.firstname,
				lastname: newValue.properties.lastname,
				recordID: newValue.properties.hs_object_id,
			});
		} else {
			// if the user cleared the autocomplete (still triggers as removing the value is technically a selection event)
			setClientDetailedViewOpen(false);

			setClientInfo({
				email: "",
				firstname: "",
				lastname: "",
				recordID: "",
			});
		}
	};

	// TODO: IMPLEMENT
	const clientInfoChanged = (event) => {
		clearFieldError(
			event.target.name === "email"
				? "clientEmail"
				: event.target.name === "firstname"
					? "clientFirstname"
					: "clientLastname",
		);

		setClientInfo((prevInfo) => ({
			...prevInfo,
			[event.target.name]: event.target.value,
		}));

		if (event.target.name === "email") {
			setClientEmailError(false);
		}
	};

	const checkEmail = async (event) => {
		const textLength = event.target.value.length;
		const emailRegex = /^\S+@\S+\.\S+$/;
		// if the current text length is less than 3, we will get too many results, so return
		if (textLength < 3 || event.target.value.match(emailRegex) === null) {
			setClientEmailError(false);
			return;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hubspot/getContactFromEmail",
				{
					email: event.target.value,
				},
			);

			if (res.data && res.data.id) {
				setClientEmailError(true);
			} else {
				setClientEmailError(false);
			}
		} catch (error) {
			errorMessageOpened(
				"Error fetching contact records from HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator.",
			);
		}
	};

	// PROPERTY INPUT HANDLER METHODS
	const propertySearchByValueSelected = (event, newValue) => {
		// if the searchByValue is selected and the user didn't just clear the autocomplete
		if (newValue && newValue.properties) {
			setPropertyDetailedViewOpen(true);

			setPropertyInfo({
				name: newValue.properties.property_name,
				address1: newValue.properties.property_address_1,
				address2: newValue.properties.property_address_2,
				city: newValue.properties.property_city,
				state: newValue.properties.property_state,
				zip: newValue.properties.property_zip_code,
				recordID: newValue.properties.hs_object_id,
			});

			clearUntouchedBuildingDescription();
			prefillBuildingDescription(newValue.properties.hs_object_id);
		} else {
			clearUntouchedBuildingDescription();
			// if the user cleared the autocomplete (still triggers as removing the value is technically a selection event)
			setPropertyDetailedViewOpen(false);

			setPropertyInfo({
				name: "",
				address1: "",
				address2: "",
				city: "",
				state: "",
				zip: "",
				recordID: "",
			});

			setPropertyOptions([]);
		}
	};

	// clears a prefilled building description the user hasn't edited, so it doesn't stay behind when the property changes
	const clearUntouchedBuildingDescription = () => {
		const prefill = scopeBackgroundPrefillRef.current;
		if (!prefill) return;
		if (scopeBackgroundRef.current.trim() === prefill.description) {
			setScopeBackground("");
		}
		setScopeBackgroundPrefill(null);
	};

	// fills an empty Scope / Background with the building description from the property's most recent RFP
	const prefillBuildingDescription = async (propertyRecordID) => {
		if (!propertyRecordID) return;

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/rfp/buildingDescription",
				{ propertyHubspotID: propertyRecordID },
			);
			const description = res.data?.description;
			// skip if nothing was found, the property has since changed (another pick, new property, draft load,
			// form reset), or the user already typed a scope
			if (
				!description ||
				propertyRecordIDRef.current !== propertyRecordID ||
				scopeBackgroundRef.current.trim()
			) {
				return;
			}

			clearFieldError("scopeBackground");
			setScopeBackground(`${description} `);
			setScopeBackgroundPrefill({
				description,
				submittedTime: res.data.submittedTime,
				recordID: propertyRecordID,
			});
		} catch (error) {
			// prefill is a convenience; the user can still type the description
			console.log("Error fetching building description: ", error);
		}
	};

	const createNewPropertyClicked = () => {
		if (createNewPropertyEnabled) {
			return;
		}

		clearUntouchedBuildingDescription();

		// close the property search bar
		setPropertySearchBarOpen(false);

		// enable the create new property view
		setCreateNewPropertyEnabled(true);
		setSearchExistingPropertyEnabled(false);

		// clear all previous values that were in before
		setPropertySearchByValue("");

		setPropertyInfo({
			name: "",
			address1: "",
			address2: "",
			city: "",
			state: "",
			zip: "",
			recordID: "",
		});

		// clear the property searchbyvalue autocomplete
		const propertySearchByValueEle =
			propertySearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator",
			)[0];
		if (propertySearchByValueEle) propertySearchByValueEle.click();

		// open the property detailed view
		setPropertyDetailedViewOpen(true);
	};

	const searchExistingPropertyClicked = (override = false) => {
		if (searchExistingPropertyEnabled && !override) {
			return;
		}

		clearUntouchedBuildingDescription();

		// open the property search bar
		setPropertySearchBarOpen(true);
		setPropertyDetailedViewOpen(false);

		// enable the search existing property view
		setSearchExistingPropertyEnabled(true);
		setCreateNewPropertyEnabled(false);

		// clear all previous values that were in before
		setPropertySearchByValue("");

		setPropertyInfo({
			name: "",
			address1: "",
			address2: "",
			city: "",
			state: "",
			zip: "",
			recordID: "",
		});
	};

	const propertyInfoChanged = (event) => {
		const fieldNameMap = {
			name: "propertyName",
			address1: "propertyAddress1",
			city: "propertyCity",
			state: "propertyState",
			zip: "propertyZip",
		};
		const nextValue =
			event.target.name === "zip"
				? formatZipCodeNumber(event.target.value)
				: event.target.value;
		if (fieldNameMap[event.target.name]) {
			clearFieldError(fieldNameMap[event.target.name]);
		}

		setPropertyInfo((prevInfo) => ({
			...prevInfo,
			[event.target.name]: nextValue,
		}));
	};

	// SERVICE INPUT HANDLER METHODS
	const genericServiceChanged = (event) => {
		clearFieldError("services");
		setServiceCheckboxes((prevInfo) => ({
			...prevInfo,
			[event.target.name]: event.target.checked,
		}));
	};

	const serviceVisualChanged = (event) => {
		clearFieldError("services");
		if (event.target.checked) {
			// when visual is checked, invasive is disabled
			setServiceCheckboxes((prevInfo) => ({
				...prevInfo,
				visual: true,
				invasive: false,
			}));

			setEnabledServices((prevInfo) => ({
				...prevInfo,
				invasive: false,
			}));

			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened("Visual cannot be combined with Invasive.");
		} else {
			// when visual is unchecked, invasive is enabled (not selected)
			setServiceCheckboxes((prevInfo) => ({
				...prevInfo,
				visual: false,
				invasive: false,
			}));

			setEnabledServices((prevInfo) => ({
				...prevInfo,
				invasive: true,
			}));

			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceInvasiveChanged = (event) => {
		clearFieldError("services");
		if (event.target.checked) {
			// when invasive is checked, visual is disabled
			setServiceCheckboxes((prevInfo) => ({
				...prevInfo,
				visual: false,
				invasive: true,
			}));

			setEnabledServices((prevInfo) => ({
				...prevInfo,
				visual: false,
			}));

			serviceMessageOpened("Invasive cannot be combined with Visual.");
		} else {
			// when invasive is unchecked, visual is enabled (not selected)
			setServiceCheckboxes((prevInfo) => ({
				...prevInfo,
				visual: false,
				invasive: false,
			}));

			setEnabledServices((prevInfo) => ({
				...prevInfo,
				visual: !serviceCheckboxes.claimSupport,
			}));

			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceRapidVisualChanged = (event) => {
		clearFieldError("services");
		setServiceCheckboxes((prevInfo) => ({
			...prevInfo,
			rapidVisual: event.target.checked,
		}));

		if (event.target.checked) {
			disableAllServicesBut(["Rapid Visual"]);
			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened(
				"Rapid Visual (Wellness Exam) is a standalone service.",
			);
		} else {
			enableAllServices();
			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceClaimSupportChanged = (event) => {
		clearFieldError("services");
		clearFieldError("claimType");
		clearFieldError("claimCause");
		if (event.target.checked) {
			setServiceCheckboxes((prevInfo) => ({
				...prevInfo,
				visual: false,
				pca: false,
				design: false,
				bidAssistance: false,
				contractAdministration: false,
				rapidVisual: false,
				claimSupport: true,
				feasibility: false,
				generalConsulting: false,
				engineeringStructural: false,
				drainage: false,
				additionalServices: false,
				windowTesting: false,
				romExercise: false,
			}));

			setEnabledServices((prevInfo) => ({
				...prevInfo,
				visual: false,
				invasive: true,
				pca: false,
				design: false,
				bidAssistance: false,
				contractAdministration: false,
				rapidVisual: false,
				claimSupport: true,
				feasibility: false,
				generalConsulting: false,
				engineeringStructural: false,
				drainage: false,
				additionalServices: false,
				windowTesting: false,
				romExercise: false,
			}));

			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened(
				"Claim Support can only be combined with Invasive.",
			);
		} else {
			setServiceCheckboxes((prevInfo) => ({
				...prevInfo,
				claimSupport: false,
			}));
			enableAllServices();
			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceFeasibilityChanged = (event) => {
		clearFieldError("services");
		setServiceCheckboxes((prevInfo) => ({
			...prevInfo,
			feasibility: event.target.checked,
		}));

		if (event.target.checked) {
			disableAllServicesBut(["Feasibility"]);
			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened("Feasibility is a standalone service.");
		} else {
			enableAllServices();
			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceGeneralConsultingChanged = (event) => {
		clearFieldError("services");
		setServiceCheckboxes((prevInfo) => ({
			...prevInfo,
			generalConsulting: event.target.checked,
		}));

		if (event.target.checked) {
			disableAllServicesBut(["General Consulting"]);
			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened("General Consulting is a standalone service.");
		} else {
			enableAllServices();
			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceEngineeringStructuralChanged = (event) => {
		clearFieldError("services");
		setServiceCheckboxes((prevInfo) => ({
			...prevInfo,
			engineeringStructural: event.target.checked,
		}));

		if (event.target.checked) {
			disableAllServicesBut(["Engineering - Structural Services"]);
			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened(
				"Engineering - Structural Services is a standalone service.",
			);
		} else {
			enableAllServices();
			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceAdditionalServicesChanged = (event) => {
		clearFieldError("services");
		setServiceCheckboxes((prevInfo) => ({
			...prevInfo,
			additionalServices: event.target.checked,
		}));

		if (event.target.checked) {
			disableAllServicesBut(["Additional Services"]);
			setServiceInvasiveNumberOfOpenings("");
			serviceMessageOpened("Additional Services are standalone.");
		} else {
			enableAllServices();
			serviceMessageClosed();
			setServiceInvasiveNumberOfOpenings("");
		}
	};

	const serviceInvasiveNumberOfOpeningsChanged = (event) => {
		setServiceInvasiveNumberOfOpenings(event.target.value);
	};

	const claimTypeChanged = (event) => {
		clearFieldError("claimType");
		setClaimType(event.target.value);
	};

	const claimCauseChanged = (event) => {
		clearFieldError("claimCause");
		setClaimCause((prevInfo) => ({
			...prevInfo,
			[event.target.name]: event.target.checked,
		}));
	};

	// SERVICE HELPER METHODS
	const enableAllServices = () => {
		setEnabledServices({
			visual: true,
			invasive: true,
			pca: true,
			design: true,
			bidAssistance: true,
			contractAdministration: true,
			rapidVisual: true,
			claimSupport: true,
			feasibility: true,
			generalConsulting: true,
			engineeringStructural: true,
			drainage: true,
			additionalServices: true,
			windowTesting: true,
			romExercise: true,
		});
	};

	const disableAllServicesBut = (services) => {
		setServiceCheckboxes({
			visual: services.includes("Visual"),
			invasive: services.includes("Invasive"),
			pca: services.includes("PCA"),
			design: services.includes("Design"),
			bidAssistance: services.includes("Bid Assistance"),
			contractAdministration: services.includes(
				"Contract Administration",
			),
			rapidVisual: services.includes("Rapid Visual"),
			claimSupport: services.includes("Claim Support"),
			feasibility: services.includes("Feasibility"),
			generalConsulting: services.includes("General Consulting"),
			engineeringStructural: services.includes(
				"Engineering - Structural Services",
			),
			drainage: services.includes("Drainage"),
			additionalServices: services.includes("Additional Services"),
			windowTesting: services.includes("Window Testing"),
			romExercise: services.includes("ROM Exercise"),
		});

		setEnabledServices({
			visual: services.includes("Visual"),
			invasive: services.includes("Invasive"),
			pca: services.includes("PCA"),
			design: services.includes("Design"),
			bidAssistance: services.includes("Bid Assistance"),
			contractAdministration: services.includes(
				"Contract Administration",
			),
			rapidVisual: services.includes("Rapid Visual"),
			claimSupport: services.includes("Claim Support"),
			feasibility: services.includes("Feasibility"),
			generalConsulting: services.includes("General Consulting"),
			engineeringStructural: services.includes(
				"Engineering - Structural Services",
			),
			drainage: services.includes("Drainage"),
			additionalServices: services.includes("Additional Services"),
			windowTesting: services.includes("Window Testing"),
			romExercise: services.includes("ROM Exercise"),
		});
	};

	// COMPONENT INPUT HANDLER METHODS
	const componentCheckboxChanged = (event) => {
		clearFieldError("components");
		setComponentCheckboxes((prevInfo) => ({
			...prevInfo,
			[event.target.name]: event.target.checked,
		}));

		event.target.checked
			? setNumComponents((n) => n + 1)
			: setNumComponents((n) => n - 1);
	};

	// COMPONENT HELPER METHODS
	const disableUncheckedComponents = () => {
		setEnabledComponents({
			BE: componentCheckboxes.BE,
			interiorUnit: componentCheckboxes.interiorUnit,
			interiorAttic: componentCheckboxes.interiorAttic,
			interiorCrawlSpace: componentCheckboxes.interiorCrawlSpace,
			roof: componentCheckboxes.roof,
			site: componentCheckboxes.site,
			carportGarage: componentCheckboxes.carportGarage,
			decks: componentCheckboxes.decks,
			windows: componentCheckboxes.windows,
			stairway: componentCheckboxes.stairway,
			plumbing: componentCheckboxes.plumbing,
			HVAC: componentCheckboxes.HVAC,
			foundation: componentCheckboxes.foundation,
			other: componentCheckboxes.other,
		});
	};

	const enableAllComponents = () => {
		setEnabledComponents({
			BE: true,
			interiorUnit: true,
			interiorAttic: true,
			interiorCrawlSpace: true,
			roof: true,
			site: true,
			carportGarage: true,
			decks: true,
			windows: true,
			stairway: true,
			plumbing: true,
			HVAC: true,
			foundation: true,
			other: true,
		});
	};

	// COST BASIS INPUT HANDLER METHODS
	const costBasisChanged = (event) => {
		setServiceCostBases((prevInfo) => ({
			...prevInfo,
			[event.target.name]: event.target.value,
		}));
	};

	// ALERT HELPER FUNCTIONS
	const errorMessageClosed = () => {
		setIsError(false);
		setErrorMessage("");
	};

	const successMessageClosed = () => {
		setIsSuccess(false);
		setSuccessMessage("");
	};

	const serviceMessageClosed = () => {
		setServiceMessageOpen(false);
		setServiceMessageText("");
	};

	const componentMessageClosed = () => {
		setComponentMessageOpen(false);
		setComponentMessageText("");
	};

	const errorMessageOpened = (errorMessage) => {
		setIsError(true);
		setErrorMessage(errorMessage);
		successMessageClosed();
	};

	const successMessageOpened = (successMessage) => {
		setIsSuccess(true);
		setSuccessMessage(successMessage);
		errorMessageClosed();
	};

	const serviceMessageOpened = (serviceMessage) => {
		setServiceMessageOpen(true);
		setServiceMessageText(serviceMessage);
	};

	const componentMessageOpened = (componentMessage) => {
		setComponentMessageOpen(true);
		setComponentMessageText(componentMessage);
	};

	const validationSummaryClosed = () => {
		setValidationIssues([]);
	};

	const clearFieldError = (fieldName) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[fieldName]) {
				return prevErrors;
			}

			const updatedErrors = { ...prevErrors };
			delete updatedErrors[fieldName];
			return updatedErrors;
		});

		setValidationIssues((prevIssues) =>
			prevIssues.filter((issue) => issue.fieldName !== fieldName),
		);
	};

	const focusValidationField = (fieldName) => {
		const fieldIdMap = {
			clientEmail: "rfpClientEmail",
			clientFirstname: "rfpClientFirstname",
			clientLastname: "rfpClientLastname",
			clientSelection: "clientSearchByFields",
			propertyName: "rfpPropertyName",
			propertyAddress1: "rfpPropertyAddress1",
			propertyCity: "rfpPropertyCity",
			propertyState: "rfpPropertyState",
			propertyZip: "rfpPropertyZip",
			propertySelection: "rfpPropertySearchByFields",
			services: "serviceDiv",
			claimType: "claimTypeGroup",
			claimCause: "claimCauseGroup",
			components: "specificComponentDiv",
			proposalSource: "rfpProposalSource",
			proposalSourceReferral: "rfpProposalSourceReferral",
			scopeBackground: "rfpScopeBackground",
			proposalDueDate: "rfpProposalDueDate",
			buildingType: "rfpBuildingType",
			oic: "rfpOIC",
		};

		const target = document.getElementById(fieldIdMap[fieldName]);
		if (!target) {
			return;
		}

		target.scrollIntoView({ behavior: "smooth", block: "center" });
		if (typeof target.focus === "function") {
			window.setTimeout(() => target.focus(), 100);
		}
	};

	// MISC INPUT HANDLER METHODS
	const proposalSourceChanged = (event) => {
		clearFieldError("proposalSource");
		if (event.target.value !== "referral") {
			clearFieldError("proposalSourceReferral");
		}
		setProposalSource(event.target.value);
	};

	const proposalSourceReferralChanged = (event) => {
		clearFieldError("proposalSourceReferral");
		setProposalSourceReferral(event.target.value);
	};

	const scopeBackgroundChanged = (event) => {
		clearFieldError("scopeBackground");
		setScopeBackground(event.target.value);
	};

	const additionalNotesChanged = (event) => {
		setAdditionalNotes(event.target.value);
	};

	const buildingTypeChanged = (event) => {
		clearFieldError("buildingType");
		setBuildingType(event.target.value);
	};

	const oicChanged = (event) => {
		clearFieldError("oic");
		setOIC(event.target.value);
	};

	const proposalDueDateChanged = (newValue) => {
		clearFieldError("proposalDueDate");
		setProposalDueDate(newValue || dayjs());
	};

	// HELP DIALOG INPUT HANDLER METHODS
	const clientInformationHelpDialogOpened = () => {
		setClientInformationHelpDialogOpen(true);
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "RFP",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the client information help dialog.",
		});
	};

	const clientInformationHelpDialogClosed = () => {
		setClientInformationHelpDialogOpen(false);
	};

	const propertyInformationHelpDialogOpened = () => {
		setPropertyInformationHelpDialogOpen(true);
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "RFP",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the property information help dialog.",
		});
	};

	const propertyInformationHelpDialogClosed = () => {
		setPropertyInformationHelpDialogOpen(false);
	};

	const proposalSummaryHelpDialogOpened = () => {
		setProposalSummaryHelpDialogOpen(true);
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "RFP",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the proposal summary help dialog.",
		});
	};

	const proposalSummaryHelpDialogClosed = () => {
		setProposalSummaryHelpDialogOpen(false);
	};

	// CLIENT API METHODS
	const clientSearchByValueChanged = useMemo(
		() =>
			debounce(async (event) => {
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setClientOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/contacts",
						{
							searchParameter: clientSearchByParameter,
							value: event.target.value,
						},
					);
					setClientOptions(res.data);
				} catch (error) {
					errorMessageOpened(
						"Error fetching contact records from HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator.",
					);
				}
				// the 500 represents the 500ms delay sent to the debounce function below
			}, 500),
		[clientSearchByParameter],
	); // this [clientSearchByParameter] is the dependency array, which tells the useMemo hook to re-render when clientSearchByParameter is updated

	const createNewClientRecordInHubspot = async () => {
		// makes sure required data is inputted
		if (
			!clientInfo.email ||
			!clientInfo.firstname ||
			!clientInfo.lastname
		) {
			errorMessageOpened(
				"Make sure client first name, client last name, and email are all filled out. If you believe this to be an error, contact your system administrator.",
			);
			return;
		}

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/addContact",
				{
					email: clientInfo.email,
					firstName: clientInfo.firstname,
					lastName: clientInfo.lastname,
				},
			);

			if (res.data?.errorResponse === "Conflict") {
				errorMessageOpened(
					`The email address ${clientInfo.email} is already in use. Please search an existing contact or use a different email address.`,
				);

				return null;
			}

			setClientInfo((prevInfo) => ({
				...prevInfo,
				recordID: res.data?.id,
			}));

			return res.data?.id;
		} catch (error) {
			// log the error
			errorMessageOpened(
				"Error creating a contact record in HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator.",
			);
		}
	};

	// PROPERTY API METHODS
	const propertySearchByValueChanged = useMemo(
		() =>
			debounce(async (event) => {
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setPropertyOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/properties",
						{
							propertyName: event.target.value,
						},
					);
					setPropertyOptions(res.data);
				} catch (error) {
					errorMessageOpened(
						"Error fetching property records in HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator.",
					);
				}
			}, 500),
		[],
	); // 500 represents the 500ms delay sent to the debounce function below

	const createNewPropertyRecordInHubspot = async () => {
		// makes sure required data is inputted
		if (
			!propertyInfo.name ||
			!propertyInfo.address1 ||
			!propertyInfo.city ||
			!propertyInfo.state ||
			!propertyInfo.zip
		) {
			return;
		}

		// make sure that don't have "create new property" enabled
		if (!createNewPropertyEnabled) {
			return;
		}

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/addProperty",
				{
					propertyName: propertyInfo.name,
					propertyAddress1: propertyInfo.address1,
					propertyAddress2: propertyInfo.address2,
					propertyCity: propertyInfo.city,
					propertyState: propertyInfo.state,
					propertyZip: propertyInfo.zip,
				},
			);

			setPropertyInfo((prevInfo) => ({
				...prevInfo,
				recordID: res.data?.id,
			}));

			return res.data?.id;
		} catch (error) {
			errorMessageOpened(
				"Error creating a new property record in HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator.",
			);
		}
	};

	// API HELPER FUNCTIONS
	const getHubspotOwner = async (ownerEmail) => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hubspot/getOwnerID",
				{
					email: ownerEmail,
				},
			);

			return res.data[0].id;
		} catch (error) {
			errorMessageOpened(
				`Error retrieving the HubSpot user record for ${ownerEmail}. Refresh your browser and try again. If the problem persists, contact your system administrator.`,
			);
			return;
		}
	};

	const createDealInHubspot = async (parameters) => {
		const addDealRes = await postAPICall(
			instance,
			accounts[0],
			"/api/hubspot/addDeal",
			{
				...parameters,
			},
		);

		return addDealRes;
	};

	const associateContactToDeal = async (dealID, contactID) => {
		const associateToDeal = await postAPICall(
			instance,
			accounts[0],
			"/api/hubspot/associateToDeal",
			{
				dealID: dealID,
				contactID: contactID,
			},
		);

		return associateToDeal;
	};

	const associatePropertyToDeal = async (dealID, propertyID) => {
		const associateToDeal = await postAPICall(
			instance,
			accounts[0],
			"/api/hubspot/associatePropertyToDeal",
			{
				dealID: dealID,
				propertyID: propertyID,
			},
		);

		return associateToDeal;
	};

	const createTaskInHubspot = async (subject, ownerID) => {
		const createTaskRes = await postAPICall(
			instance,
			accounts[0],
			"/api/hubspot/createTask",
			{
				subject: subject,
				ownerID: ownerID,
				createdTime: dayjs().startOf("day").valueOf(),
			},
		);

		return createTaskRes;
	};

	const associateDealToTask = async (dealID, taskID) => {
		const associateToTask = await postAPICall(
			instance,
			accounts[0],
			"/api/hubspot/associateToTask",
			{
				dealID: dealID,
				taskID: taskID,
			},
		);

		return associateToTask;
	};

	const createDropboxFolder = async (hsDealID, hsPropertyID) => {
		// sends this data to Express server to relay to Make scenario
		sendCreateDropboxFolderWebhook(hsDealID, hsPropertyID);
	};

	// UI HELPER FUNCTIONS
	const clearInputFields = () => {
		setFieldErrors({});
		setValidationIssues([]);

		// client property search bar states
		searchExistingClientClicked(true);
		searchExistingPropertyClicked(true);

		// service checkboxes
		setServiceCheckboxes({
			visual: false,
			invasive: false,
			pca: false,
			design: false,
			bidAssistance: false,
			contractAdministration: false,
			rapidVisual: false,
			claimSupport: false,
			feasibility: false,
			generalConsulting: false,
			engineeringStructural: false,
			drainage: false,
			additionalServices: false,
			windowTesting: false,
			romExercise: false,
		});
		setClaimType("");

		// component checkboxes
		setComponentCheckboxes({
			BE: false,
			interiorUnit: false,
			interiorAttic: false,
			interiorCrawlSpace: false,
			roof: false,
			site: false,
			carportGarage: false,
			decks: false,
			windows: false,
			stairway: false,
			plumbing: false,
			HVAC: false,
			foundation: false,
			other: false,
		});

		// service cost basis radio buttons
		setServiceCostBases({
			visual: "flatFee",
			invasive: "flatFee",
			pca: "flatFee",
			design: "flatFee",
			bidAssistance: "timeAndCost",
			contractAdministration: "timeAndCost",
			rapidVisual: "flatFee",
			claimSupport: "timeAndCost",
			feasibility: "flatFee",
			generalConsulting: "timeAndCost",
			engineeringStructural: "timeAndCost",
			drainage: "timeAndCost",
			additionalServices: "timeAndCost",
			windowTesting: "flatFee",
			romExercise: "flatFee",
		});

		// Enable services and components
		enableAllServices();
		enableAllComponents();

		// Clear Messages
		serviceMessageClosed();
		componentMessageClosed();

		setNumComponents(0);

		// various input fields
		setScopeBackground("");
		setScopeBackgroundPrefill(null);
		setAdditionalNotes("");
		setOIC("");
		setProposalDueDate(dayjs());

		// clear client searchbyvalue autocomplete
		const clientSearchByValueEle =
			clientSearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator",
			)[0];
		if (clientSearchByValueEle) clientSearchByValueEle.click();

		// clear property searchbyvalue autocomplete
		const propertySearchByValueEle =
			propertySearchByValueRef.current.getElementsByClassName(
				"MuiAutocomplete-clearIndicator",
			)[0];
		if (propertySearchByValueEle) propertySearchByValueEle.click();
	};

	// MISC HELPER FUNCTIONS
	// This is used for limiting the amount of api requests, it is called when a user stops changing the input textfield for a given amount of time (wait)
	function debounce(callback, wait) {
		// creates/resets a timeout every time the function is called
		let timeoutId = null;
		return (...args) => {
			// if the function is called again before the timeout is up, clear the timeout and start it over
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(() => {
				// when the timeout is up, call the function with the arguments
				callback.apply(null, args);
			}, wait);
		};
	}

	const trimString = (value) =>
		typeof value === "string" ? value.trim() : value;

	const isBlank = (value) => {
		if (typeof value === "string") {
			return trimString(value).length === 0;
		}
		return value === null || value === undefined;
	};

	const validateInputFields = () => {
		const nextFieldErrors = {};
		const nextValidationIssues = [];
		const trimmedPropertyName = trimString(propertyInfo.name || "");
		const addFieldError = (fieldName, message) => {
			if (nextFieldErrors[fieldName]) {
				return;
			}
			nextFieldErrors[fieldName] = message;
			nextValidationIssues.push({ fieldName, message });
		};

		// validate client information
		if (createNewClientEnabled) {
			if (isBlank(clientInfo.email)) {
				addFieldError(
					"clientEmail",
					"Client Information > Email: Enter an email address.",
				);
			}
			if (isBlank(clientInfo.firstname)) {
				addFieldError(
					"clientFirstname",
					"Client Information > First Name: Enter a first name.",
				);
			}
			if (isBlank(clientInfo.lastname)) {
				addFieldError(
					"clientLastname",
					"Client Information > Last Name: Enter a last name.",
				);
			}
		} else if (!clientInfo.recordID) {
			addFieldError(
				"clientSelection",
				"Client Information: Select an existing client or switch to Create New Client.",
			);
		}

		if (clientEmailError) {
			addFieldError(
				"clientEmail",
				"Client Information > Email: This email already exists. Select that client or use a different email.",
			);
		}

		// validate property information
		if (createNewPropertyEnabled) {
			if (isBlank(propertyInfo.name)) {
				addFieldError(
					"propertyName",
					"Property Information > Property Name: Enter a property name.",
				);
			}
			if (isBlank(propertyInfo.address1)) {
				addFieldError(
					"propertyAddress1",
					"Property Information > Address Line 1: Enter an address.",
				);
			}
			if (isBlank(propertyInfo.city)) {
				addFieldError(
					"propertyCity",
					"Property Information > City: Enter a city.",
				);
			}
			if (isBlank(propertyInfo.state)) {
				addFieldError(
					"propertyState",
					"Property Information > State: Select a state.",
				);
			}
			if (isBlank(propertyInfo.zip)) {
				addFieldError(
					"propertyZip",
					"Property Information > ZIP: Enter a ZIP code.",
				);
			}
		} else if (!propertyInfo.recordID) {
			addFieldError(
				"propertySelection",
				"Property Information: Select an existing property or switch to Create New Property.",
			);
		}

		if (
			createNewPropertyEnabled &&
			!isBlank(propertyInfo.name) &&
			!trimmedPropertyName.match(
				/^(?=.{1,255}$)(?!.*[\\\/])(?!.*\s$)[A-Za-z0-9 ()\[\]{}_.,&'@!#$%^+=~`-]+$/,
			) // this regex complies with dropbox folder name rules, check confluence for more details
		) {
			addFieldError(
				"propertyName",
				"Property Information > Property Name: Use letters, numbers, spaces, and punctuation; slashes are not allowed.",
			);
		}

		// validate service checkboxes
		var serviceCount = countPhases();
		if (serviceCount > 4) {
			addFieldError(
				"services",
				`Proposal Summary > Services: Select at most 4 services (currently ${serviceCount}).`,
			);
		}

		// validate component checkboxes
		var componentCount = countComponents();
		if (componentCount > 2) {
			addFieldError(
				"components",
				`Proposal Summary > Specific Component: Select at most 2 components (currently ${componentCount}).`,
			);
		}

		// service checkboxes
		if (
			!serviceCheckboxes.visual &&
			!serviceCheckboxes.invasive &&
			!serviceCheckboxes.pca &&
			!serviceCheckboxes.design &&
			!serviceCheckboxes.bidAssistance &&
			!serviceCheckboxes.contractAdministration &&
			!serviceCheckboxes.rapidVisual &&
			!serviceCheckboxes.claimSupport &&
			!serviceCheckboxes.feasibility &&
			!serviceCheckboxes.generalConsulting &&
			!serviceCheckboxes.engineeringStructural &&
			!serviceCheckboxes.drainage &&
			!serviceCheckboxes.additionalServices &&
			!serviceCheckboxes.windowTesting &&
			!serviceCheckboxes.romExercise
		) {
			addFieldError(
				"services",
				"Proposal Summary > Services: Select at least one service.",
			);
		}

		if (serviceCheckboxes.claimSupport && isBlank(claimType)) {
			addFieldError(
				"claimType",
				"Proposal Summary > Claim Type: Choose COA or Proposal when Claim Support is selected.",
			);
		}

		if (
			serviceCheckboxes.claimSupport &&
			!Object.values(claimCause).some(Boolean)
		) {
			addFieldError(
				"claimCause",
				"Proposal Summary > Claim Cause: Select at least one of Fire, Flood, Water Damage, Structural, or Design when Claim Support is selected.",
			);
		}

		// component checkboxes
		if (
			!componentCheckboxes.BE &&
			!componentCheckboxes.interiorUnit &&
			!componentCheckboxes.interiorAttic &&
			!componentCheckboxes.interiorCrawlSpace &&
			!componentCheckboxes.roof &&
			!componentCheckboxes.site &&
			!componentCheckboxes.carportGarage &&
			!componentCheckboxes.decks &&
			!componentCheckboxes.windows &&
			!componentCheckboxes.stairway &&
			!componentCheckboxes.plumbing &&
			!componentCheckboxes.HVAC &&
			!componentCheckboxes.foundation &&
			!componentCheckboxes.other
		) {
			addFieldError(
				"components",
				"Proposal Summary > Specific Component: Select at least one component.",
			);
		}

		// proposal source
		if (!proposalSource) {
			addFieldError(
				"proposalSource",
				"Proposal Summary > RFP Source: Select where this proposal came from.",
			);
		}

		if (proposalSource === "referral" && isBlank(proposalSourceReferral)) {
			addFieldError(
				"proposalSourceReferral",
				"Proposal Summary > Referral Source: Enter who referred this proposal.",
			);
		}

		// scope/background text field
		if (isBlank(scopeBackground)) {
			addFieldError(
				"scopeBackground",
				"Proposal Summary > Scope/Background: Enter project scope details.",
			);
		}

		// proposal due date datepicker
		if (!proposalDueDate) {
			addFieldError(
				"proposalDueDate",
				"Proposal Summary > Proposal Due Date: Select a due date.",
			);
		}

		// building type
		if (!buildingType) {
			addFieldError(
				"buildingType",
				"Proposal Summary > Building Type: Select a building type.",
			);
		}

		// OIC select
		if (!oic) {
			addFieldError("oic", "Proposal Summary > OIC: Select an OIC.");
		}

		setFieldErrors(nextFieldErrors);
		setValidationIssues(nextValidationIssues);

		if (nextValidationIssues.length > 0) {
			focusValidationField(nextValidationIssues[0].fieldName);
			errorMessageOpened(
				`Please fix the highlighted fields (${nextValidationIssues.length}) before submitting.`,
			);
			return false;
		}

		// if all good, return true
		return true;
	};

	const createDealName = () => {
		let dealName = `${propertyInfo.name}: `;

		// creates an array of all beaut-ified components
		let components = createComponentArray();
		// if it's not empty, add the contents of the array (comma delimited) and a colon to the end of this section of the deal name
		if (components.length > 0) {
			dealName += components.join(", ");
			dealName += ": ";
		}

		// creates an array of all beaut-ified services
		let services = createServiceArray();
		// there won't be a colon at the end of this section so we add the contents (comma delimited)
		dealName += services.join(", ");

		return dealName;
	};

	const createComponentArray = () => {
		var components = [];
		if (componentCheckboxes.BE) components.push("Building Envelope");
		if (componentCheckboxes.interiorUnit)
			components.push("Interior - Unit");
		if (componentCheckboxes.interiorAttic)
			components.push("Interior - Attic");
		if (componentCheckboxes.interiorCrawlSpace)
			components.push("Interior - Crawl Space");
		if (componentCheckboxes.roof) components.push("Roof");
		if (componentCheckboxes.site) components.push("Site");
		if (componentCheckboxes.carportGarage)
			components.push("Carport / Garage");
		if (componentCheckboxes.decks) components.push("Decks / Walkways");
		if (componentCheckboxes.windows) components.push("Windows");
		if (componentCheckboxes.stairway) components.push("Stairway");
		if (componentCheckboxes.plumbing) components.push("Plumbing / Repipe");
		if (componentCheckboxes.HVAC) components.push("HVAC");
		if (componentCheckboxes.foundation) components.push("Foundation");
		if (componentCheckboxes.other) components.push("Other");

		return components;
	};

	const createServiceArray = () => {
		var services = [];
		if (serviceCheckboxes.visual) services.push("Visual");
		if (serviceCheckboxes.invasive) services.push("Invasive");
		if (serviceCheckboxes.pca) services.push("PCA");
		if (serviceCheckboxes.design) services.push("Design");
		if (serviceCheckboxes.bidAssistance) services.push("Bid Assistance");
		if (serviceCheckboxes.contractAdministration)
			services.push("Contract Administration");
		if (serviceCheckboxes.rapidVisual)
			services.push("Rapid Visual (Wellness Exam)");
		if (serviceCheckboxes.claimSupport) services.push("Claim Support");
		if (serviceCheckboxes.feasibility) services.push("Feasibility");
		if (serviceCheckboxes.generalConsulting)
			services.push("General Consulting");
		if (serviceCheckboxes.engineeringStructural)
			services.push("Engineering - Structural Services");
		if (serviceCheckboxes.drainage) services.push("Drainage");
		if (serviceCheckboxes.additionalServices)
			services.push("Additional Services");
		if (serviceCheckboxes.windowTesting) services.push("Window Testing");
		if (serviceCheckboxes.romExercise) services.push("ROM Exercise");

		return services;
	};

	const determinePhaseCostBasises = () => {
		var phaseCostBasises = [];

		// add all cost bases to the array
		for (var key in serviceCostBases) {
			if (serviceCheckboxes[key]) {
				phaseCostBasises.push(serviceCostBases[key]);
			}
		}

		// beautify the cost bases
		for (var i = 0; i < phaseCostBasises.length; i++) {
			if (phaseCostBasises[i] === "timeAndCost") {
				phaseCostBasises[i] = "Time and Cost";
			} else if (phaseCostBasises[i] === "flatFee") {
				phaseCostBasises[i] = "Flat Fee";
			}
		}

		return phaseCostBasises;
	};

	const countPhases = () => {
		var count = 0;

		for (var key in serviceCheckboxes) {
			if (serviceCheckboxes[key]) count++;
		}

		return count;
	};

	const countComponents = () => {
		var count = 0;

		for (var key in componentCheckboxes) {
			if (componentCheckboxes[key]) count++;
		}

		return count;
	};

	// SUBMIT RFP FORM PRESSED
	const submitRFPFormPressed = async () => {
		// log the time the submit button was clicked
		loggingPayload.current.submitClickedTime = getCurrentTimeUTC();

		try {
			// this adds a loading spinner and disables the button
			setIsSubmitting(true);

			// retrieve MSAL user email and handle error if necessary
			const userRes = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/byID",
				{ id: employeeID },
			);

			if (
				userRes.data.length === 0 ||
				userRes.data.error ||
				!userRes.data[0].last_name
			) {
				errorMessageOpened(
					"Couldn't verify your user profile for this submission. Your form data is still on this page. Please try Submit again.",
				);
				setIsSubmitting(false);
				await submitRFPLog({
					errorMessage:
						"Error retrieving user's Microsoft profile. Refresh your browser and try again. If the problem persists, contact your system administrator.",
				});
				return;
			} else {
				// log the time the user email was retrieved
				loggingPayload.current.userEmailRetrievedTime =
					getCurrentTimeUTC();
			}

			// this just checks to see if all the input fields are valid
			const inputsValid = validateInputFields();
			if (!inputsValid) {
				// no rfp log created because input validation failed, error message handled in validateInputFields()
				setIsSubmitting(false);
				// log the error message
				await submitRFPLog({
					errorMessage:
						"Error validating input fields. User did not fill out required fields or filled them out incorrectly.",
				});
				return;
			}

			// this checks to see if an existing client is selected, and if not, creates a new client record in HubSpot
			var associatedClientRecordID = clientInfo.recordID;
			if (!clientInfo.recordID) {
				associatedClientRecordID =
					await createNewClientRecordInHubspot();

				// if this returned -2, then the email address is already in use

				// if this returned a nullable value, then add to the automation log and return
				if (!associatedClientRecordID) {
					await submitRFPLog({
						errorMessage: "Error creating client record in Hubspot",
					});
					errorMessageOpened(
						"Couldn't create the client in HubSpot. Your form data is still on this page. Please try Submit again.",
					);
					setIsSubmitting(false);
					return;
				} else {
					// log the time the client record was created/found
					loggingPayload.current.clientHubspotCreatedFoundTime =
						getCurrentTimeUTC();
				}
			} else {
				// existing client was selected, still record found time
				loggingPayload.current.clientHubspotCreatedFoundTime =
					getCurrentTimeUTC();
			}

			// this checks to see if an existing property is selected, and if not, creates a new property record in HubSpot
			var associatedPropertyRecordID = propertyInfo.recordID;
			if (!propertyInfo.recordID) {
				associatedPropertyRecordID =
					await createNewPropertyRecordInHubspot();
				// if this returned a nullable value, then add to the automation log and return
				if (!associatedPropertyRecordID) {
					await submitRFPLog({
						errorMessage:
							"Error creating property record in Hubspot",
					});
					errorMessageOpened(
						"Couldn't create the property in HubSpot. Your form data is still on this page. Please try Submit again.",
					);
					setIsSubmitting(false);
					return;
				} else {
					// log the time the property record was created/found
					loggingPayload.current.propertyHubspotCreatedFoundTime =
						getCurrentTimeUTC();
					// log the new record's ID so its building description can prefill later RFPs
					loggingPayload.current.propertyHubspotID =
						associatedPropertyRecordID;
				}
			} else {
				// existing property was selected, still record found time
				loggingPayload.current.propertyHubspotCreatedFoundTime =
					getCurrentTimeUTC();
			}

			// simple function to concatenate the deal name (property name: components: services [Property: Project: Phase])
			const dealName = createDealName();

			// gets the hubspot ID from the user's email, this will be used to set the current user (person submitting RFP form) as the owner of the deal in HS
			const ownerID = await getHubspotOwner(userRes?.data?.[0]?.email);

			if (ownerID === "error" || !ownerID) {
				await submitRFPLog({
					errorMessage: "Error retrieving HubSpot owner ID",
				});
				errorMessageOpened(
					"Couldn't resolve the HubSpot owner for this submission. Your form data is still on this page. Please try Submit again.",
				);
				setIsSubmitting(false);
				return;
			} else {
				// log the time the hubspot owner ID was retrieved
				loggingPayload.current.hubspotOwnerRetrievedTime =
					getCurrentTimeUTC();
			}

			// this changes the cost basis from the radio buttons to a more human-readable format
			// there will be max four phases in an RFP, so we prepare the cost bases for all four phases, regardless of how many are selected
			const phaseCostBasises = determinePhaseCostBasises();
			const phase1CostBasis = phaseCostBasises[0]
				? phaseCostBasises[0]
				: "";
			const phase2CostBasis = phaseCostBasises[1]
				? phaseCostBasises[1]
				: "";
			const phase3CostBasis = phaseCostBasises[2]
				? phaseCostBasises[2]
				: "";
			const phase4CostBasis = phaseCostBasises[3]
				? phaseCostBasises[3]
				: "";

			const claimSupportMetadata = [];
			if (serviceCheckboxes.claimSupport && claimType) {
				claimSupportMetadata.push(
					`[Claim Support Claim Type: ${claimType}]`,
				);
			}
			const selectedClaimCauses = [
				claimCause.fire ? "Fire" : null,
				claimCause.flood ? "Flood" : null,
				claimCause.waterDamage ? "Water Damage" : null,
				claimCause.structural ? "Structural" : null,
				claimCause.design ? "Design" : null,
				claimCause.cd ? "CD (construction defect)" : null,
			].filter(Boolean);
			if (serviceCheckboxes.claimSupport && selectedClaimCauses.length) {
				claimSupportMetadata.push(
					`[Claim Support Claim Cause: ${selectedClaimCauses.join(", ")}]`,
				);
			}
			const augmentedScopeBackground = claimSupportMetadata.length
				? `${scopeBackground ? scopeBackground + "\n" : ""}${claimSupportMetadata.join("\n")}`
				: scopeBackground;

			// detemine phase name for deal field
			const phases = createServiceArray();
			const phase1Name = phases[0] ? phases[0] : "";
			const phase2Name = phases[1] ? phases[1] : "";
			const phase3Name = phases[2] ? phases[2] : "";
			const phase4Name = phases[3] ? phases[3] : "";

			// this is the project name, which is a list of all the components selected
			var projectName = createComponentArray();
			projectName = projectName.join(", ");

			// this is map of information we send to the hubspot API
			const addDealParams = {
				dealName: dealName ? dealName : "",
				ownerID: ownerID,
				invasiveOpenings: serviceInvasiveNumberOfOpenings
					? serviceInvasiveNumberOfOpenings
					: "",
				phase1Name: phase1Name,
				phase2Name: phase2Name,
				phase3Name: phase3Name,
				phase4Name: phase4Name,
				phase1CostBasis: phase1CostBasis,
				phase2CostBasis: phase2CostBasis,
				phase3CostBasis: phase3CostBasis,
				phase4CostBasis: phase4CostBasis,
				propertyName: propertyInfo.name ? propertyInfo.name : "",
				propertyAddress: `${propertyInfo.address1} ${
					propertyInfo.address2 ? propertyInfo.address2 : ""
				}`,
				propertyCity: propertyInfo.city ? propertyInfo.city : "",
				propertyState: propertyInfo.state ? propertyInfo.state : "",
				propertyZip: propertyInfo.zip ? propertyInfo.zip : "",
				projectName: projectName ? projectName : "",
				scopeBackground: augmentedScopeBackground
					? augmentedScopeBackground
					: "",
				additionalNotes: additionalNotes ? additionalNotes : "",
				oic: oic ? oic : "",
				proposalDueDate: proposalDueDate
					? proposalDueDate.utc(true).startOf("day").valueOf()
					: "",
				proposalSource: rfpSourceMap[proposalSource]
					? rfpSourceMap[proposalSource]
					: "",
				proposalSourceReferral: proposalSourceReferral
					? proposalSourceReferral
					: "",
				buildingType: buildingType ? buildingType : "",
			};
			// this createDealInHubspot function sends the information to Express server which then relays it to the hubspot API
			const addDealRes = await createDealInHubspot(addDealParams);
			// if the response is null or doesn't have an ID, then we add a failure to the automation log and return
			if (!addDealRes.data || !addDealRes.data.id) {
				await submitRFPLog({
					errorMessage: "Error creating a deal record in HubSpot",
				});
				errorMessageOpened(
					"Couldn't create the HubSpot deal. Your form data is still on this page. Please try Submit again.",
				);
				setIsSubmitting(false);
				return;
			} else {
				// log the time the deal was created
				loggingPayload.current.hubspotDealCreatedTime =
					getCurrentTimeUTC();
			}

			// this associateContactToDeal function sends the contact ID and deal ID to Express server which relays to the hubspot API
			const contactAssociationRes = await associateContactToDeal(
				addDealRes.data.id,
				associatedClientRecordID,
			);
			// if the response is null or doesn't have a results field, then we add a failure to the automation log and return
			if (
				!contactAssociationRes.data ||
				!contactAssociationRes.data.results
			) {
				await submitRFPLog({
					errorMessage:
						"Error associating the contact to the deal in HubSpot",
				});
				errorMessageOpened(
					"Couldn't attach the client to the HubSpot deal. Your form data is still on this page. Please try Submit again.",
				);
				setIsSubmitting(false);
				return;
			} else {
				// log the time the contact was associated to the deal
				loggingPayload.current.hubspotAssociateContactDealTime =
					getCurrentTimeUTC();
			}

			// this associateContactToDeal function sends the contact ID and deal ID to Express server which relays to the hubspot API
			const propertyAssociationRes = await associatePropertyToDeal(
				addDealRes.data.id,
				associatedPropertyRecordID,
			);
			// if the response is null or doesn't have a results field, then we add a failure to the automation log and return
			if (
				!propertyAssociationRes.data ||
				!propertyAssociationRes.data.results
			) {
				await submitRFPLog({
					errorMessage:
						"Error associating the property to the deal in HubSpot",
				});
				errorMessageOpened(
					"Couldn't attach the property to the HubSpot deal. Your form data is still on this page. Please try Submit again.",
				);
				setIsSubmitting(false);
				return;
			} else {
				loggingPayload.current.hubspotAssociatePropertyDealTime =
					getCurrentTimeUTC();
			}

			// this line determines the Client Relations specialist that reviews the deal based on the OIC, Kelli or Kerry
			let primaryTaskOwnerEmail =
				oic === "OR"
					? "kerrys@j2consultants.com"
					: "kellig@j2consultants.com";
			const taskOwnerEmails =
				primaryTaskOwnerEmail === "kellig@j2consultants.com"
					? [primaryTaskOwnerEmail, "MarenJ@j2consultants.com"]
					: [primaryTaskOwnerEmail];

			for (const taskOwnerEmail of taskOwnerEmails) {
				// this line gets the hubspot ID from the Client Relations specialist's email
				const taskOwnerID = await getHubspotOwner(taskOwnerEmail);
				if (taskOwnerID === "error" || !taskOwnerID) {
					await submitRFPLog({
						errorMessage: `Error retrieving the HubSpot user record for ${taskOwnerEmail}.`,
					});
					errorMessageOpened(
						`Couldn't retrieve the HubSpot user record for ${taskOwnerEmail}. Your form data is still on this page. Please try Submit again.`,
					);
					setIsSubmitting(false);
					return;
				}
				// this createTaskInHubspot function sends the task information to Express server which relays to the hubspot API.
				// This is a task for the Client Relations specialist to review the deal
				const createTaskRes = await createTaskInHubspot(
					`Create Proposal: ${dealName}`,
					taskOwnerID,
				);
				// if the response is null or doesn't have an ID, then we add a failure to the automation log and return
				if (!createTaskRes.data || !createTaskRes.data.id) {
					await submitRFPLog({
						errorMessage:
							"Error creating a task record in HubSpot for " +
							taskOwnerEmail +
							" in HubSpot.",
					});
					setIsSubmitting(false);
					errorMessageOpened(
						"Error creating a task record in HubSpot for " +
							taskOwnerEmail +
							" in HubSpot. Your form data is still on this page. Please try Submit again.",
					);
					return;
				} else {
					loggingPayload.current.hubspotTaskCreatedTime =
						getCurrentTimeUTC();
				}

				// This is associating the task to the deal in HubSpot
				const taskAssociationRes = await associateDealToTask(
					addDealRes.data.id,
					createTaskRes.data.id,
				);
				if (
					!taskAssociationRes.data ||
					!taskAssociationRes.data.results
				) {
					await submitRFPLog({
						errorMessage:
							"Error associating the task to the deal in HubSpot",
					});
					setIsSubmitting(false);
					errorMessageOpened(
						"Couldn't associate the HubSpot task to the deal. Your form data is still on this page. Please try Submit again.",
					);
					return;
				} else {
					loggingPayload.current.hubspotAssociateTaskDealTime =
						getCurrentTimeUTC();
				}
			}

			// This creates the Dropbox folder for the deal via a webhook sent to the Express server which relays it to a Make scenario
			createDropboxFolder(addDealRes.data.id, associatedPropertyRecordID);

			// finally, we add submitted time to the automation log and submit the RFP log
			successMessageOpened("RFP form submitted successfully.");
			loggingPayload.current.submittedTime = getCurrentTimeUTC();
			setIsSubmitting(false);
			await submitRFPLog();
			resetFormForNewRFP();
		} catch (error) {
			// If there are any unspecified errors, we add a failure to the automation log and display an error message to the user
			await submitRFPLog({
				errorMessage:
					"Undefined error in submitting the RFP form: " + error,
			});
			errorMessageOpened(
				"Submission failed due to an unexpected error. Your form data is still on this page. Please try Submit again.",
			);
		}
	};

	// DROPBOX WEBHOOK FUNCTIONS
	const sendCreateDropboxFolderWebhook = async (hsDealID, hsPropertyID) => {
		try {
			var oicID = 1;
			switch (oic) {
				case "WA":
					oicID = 1;
					break;
				case "OR":
					oicID = 2;
					break;
				case "UT":
					oicID = 3;
					break;
				default:
					oicID = 1;
			}

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/webhooks/createDropboxFolder",
				{
					oicID: oicID,
					hsDealID: hsDealID,
					hsPropertyID: hsPropertyID,
					submittedByID: employeeID,
					additionalNotes: additionalNotes,
					scopeBackground: scopeBackground,
					proposalDueDate: proposalDueDate
						? proposalDueDate.utc(true).startOf("day").valueOf()
						: "",
				},
			);
		} catch (error) {
			await submitRFPLog({
				errorMessage:
					"Error sending webhook to create Dropbox folder: " + error,
			});
			console.log("Error sending webhook: ", error);
		}
	};

	const submitRFPLog = async (payloadOverride = {}) => {
		const claimSupportMetadata = [];
		if (serviceCheckboxes.claimSupport && claimType) {
			claimSupportMetadata.push(
				`[Claim Support Claim Type: ${claimType}]`,
			);
		}
		const selectedClaimCauses = [
			claimCause.fire ? "Fire" : null,
			claimCause.flood ? "Flood" : null,
			claimCause.waterDamage ? "Water Damage" : null,
			claimCause.structural ? "Structural" : null,
			claimCause.design ? "Design" : null,
		].filter(Boolean);
		if (serviceCheckboxes.claimSupport && selectedClaimCauses.length) {
			claimSupportMetadata.push(
				`[Claim Support Claim Cause: ${selectedClaimCauses.join(", ")}]`,
			);
		}
		const augmentedScopeBackground = claimSupportMetadata.length
			? `${scopeBackground ? scopeBackground + "\n" : ""}${claimSupportMetadata.join("\n")}`
			: scopeBackground;
		try {
			await postAPICall(instance, accounts[0], "/api/logs/rfp", {
				submittedByEmployeeID: employeeID,
				draftID: currentDraftID,

				clientLookupMode: createNewClientEnabled ? "new" : "existing", // 'existing' | 'new' | 'unknown'
				clientFirstName: clientInfo.firstname,
				clientLastName: clientInfo.lastname,
				clientEmail: clientInfo.email,
				clientHubspotID: clientInfo.recordID,

				propertyLookupMode: createNewPropertyEnabled
					? "new"
					: "existing", // 'existing' | 'new' | 'unknown'
				propertyName: propertyInfo.name,
				propertyAddressLine1: propertyInfo.address1,
				propertyAddressLine2: propertyInfo.address2,
				propertyCity: propertyInfo.city,
				propertyState: propertyInfo.state,
				propertyZip: propertyInfo.zip,
				propertyHubspotID: propertyInfo.recordID,

				service: createServiceArrayForLogging(),
				specificComponent: createComponentArrayForLogging(),

				proposalSource: proposalSource,
				scopeBackground: augmentedScopeBackground,
				additionalNotes: additionalNotes,
				buildingType: buildingType,
				oic: oic,
				proposalDueDate: proposalDueDate,

				modifiedBy: employeeID,

				// logging fields
				...loggingPayload.current,
				// overrides, freshest values last (wins on conflict)
				...payloadOverride,
			});

			clearLoggingFields();
		} catch (error) {
			postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID: employeeID,
				date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
				message: "Error logging RFP submission",
				success: false,
				tool_name: "RFP",
			});
		}
	};

	const clearLoggingFields = () => {
		loggingPayload.current = {
			submitClickedTime: "",
			submittedTime: "",
			errorMessage: "",
			userEmailRetrievedTime: "",
			clientHubspotCreatedFoundTime: "",
			propertyHubspotCreatedFoundTime: "",
			hubspotOwnerRetrievedTime: "",
			hubspotDealCreatedTime: "",
			hubspotAssociateContactDealTime: "",
			hubspotAssociatePropertyDealTime: "",
			hubspotTaskCreatedTime: "",
			hubspotAssociateTaskDealTime: "",
		};
	};

	const createServiceArrayForLogging = () => {
		const services = [];
		if (serviceCheckboxes.visual) services.push("Visual");
		if (serviceCheckboxes.invasive) services.push("Invasive");
		if (serviceCheckboxes.pca) services.push("PCA");
		if (serviceCheckboxes.design) services.push("Design");
		if (serviceCheckboxes.bidAssistance) services.push("Bid Assistance");
		if (serviceCheckboxes.contractAdministration)
			services.push("Contract Administration");
		if (serviceCheckboxes.rapidVisual)
			services.push("Rapid Visual (Wellness Exam)");
		if (serviceCheckboxes.claimSupport) services.push("Claim Support");
		if (serviceCheckboxes.feasibility) services.push("Feasibility");
		if (serviceCheckboxes.generalConsulting)
			services.push("General Consulting");
		if (serviceCheckboxes.engineeringStructural)
			services.push("Engineering - Structural Services");
		if (serviceCheckboxes.drainage) services.push("Drainage");
		if (serviceCheckboxes.additionalServices)
			services.push("Additional Services");
		if (serviceCheckboxes.windowTesting) services.push("Window Testing");
		if (serviceCheckboxes.romExercise) services.push("ROM Exercise");
		return services.join(", ");
	};

	const createComponentArrayForLogging = () => {
		const components = [];
		if (componentCheckboxes.BE) components.push("Building Envelope");
		if (componentCheckboxes.interiorUnit)
			components.push("Interior - Unit");
		if (componentCheckboxes.interiorAttic)
			components.push("Interior - Attic");
		if (componentCheckboxes.interiorCrawlSpace)
			components.push("Interior - Crawl Space");
		if (componentCheckboxes.roof) components.push("Roof");
		if (componentCheckboxes.site) components.push("Site");
		if (componentCheckboxes.carportGarage)
			components.push("Carport / Garage");
		if (componentCheckboxes.decks) components.push("Decks / Walkways");
		if (componentCheckboxes.windows) components.push("Windows");
		if (componentCheckboxes.stairway) components.push("Stairway");
		if (componentCheckboxes.plumbing) components.push("Plumbing / Repipe");
		if (componentCheckboxes.HVAC) components.push("HVAC");
		if (componentCheckboxes.foundation) components.push("Foundation");
		return components.join(", ");
	};

	// DRAFT FUNCTIONS
	const parseJSONField = (value, fallback) => {
		if (!value) return fallback;
		if (typeof value === "object") return value;
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	};

	const logDraftAutomation = async (success, message) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID,
				success,
				message,
				tool_name: "RFP Drafts",
			});
		} catch (error) {
			console.log("Error logging draft automation: ", error);
		}
	};

	const buildDraftFormFields = () => ({
		clientLookupMode: createNewClientEnabled ? "new" : "existing",
		clientFirstName: clientInfo.firstname,
		clientLastName: clientInfo.lastname,
		clientEmail: clientInfo.email,
		clientHubspotID: clientInfo.recordID,

		propertyLookupMode: createNewPropertyEnabled ? "new" : "existing",
		propertyName: propertyInfo.name,
		propertyAddressLine1: propertyInfo.address1,
		propertyAddressLine2: propertyInfo.address2,
		propertyCity: propertyInfo.city,
		propertyState: propertyInfo.state,
		propertyZip: propertyInfo.zip,
		propertyHubspotID: propertyInfo.recordID,

		service: createServiceArrayForLogging(),
		specificComponent: createComponentArrayForLogging(),
		formState: {
			serviceCheckboxes,
			serviceCostBases,
			serviceInvasiveNumberOfOpenings,
			componentCheckboxes,
		},

		claimType,
		claimCause,

		proposalSource,
		proposalSourceReferral,
		scopeBackground,
		additionalNotes,
		buildingType,
		oic,
		proposalDueDate: proposalDueDate
			? proposalDueDate.utc(true).startOf("day").format("YYYY-MM-DD")
			: null,
	});

	// Compared against whichever baseline applies: the last loaded/saved
	// state of a draft, or (with no draft loaded) the form's just-reset/
	// freshly-mounted state - so a brand-new, never-saved RFP with real
	// content in it is also flagged as having unsaved changes.
	const currentFormSnapshot = JSON.stringify({
		draftName: currentDraftName,
		...buildDraftFormFields(),
	});
	const draftBaselineSnapshot = currentDraftID
		? lastSavedDraftSnapshotRef.current
		: pristineFormSnapshotRef.current;
	const isDraftDirty = Boolean(
		draftBaselineSnapshot !== null &&
			currentFormSnapshot !== draftBaselineSnapshot,
	);
	isDraftDirtyRef.current = isDraftDirty;

	const saveDraft = async (draftName) => {
		setIsSavingDraft(true);
		try {
			const payload = {
				employeeID,
				draftName,
				...buildDraftFormFields(),
			};
			if (currentDraftID) {
				await putAPICall(
					instance,
					accounts[0],
					`/api/rfp-drafts/${currentDraftID}`,
					payload,
				);
				logDraftAutomation(
					true,
					`Draft "${draftName}" updated (id ${currentDraftID})`,
				);
			} else {
				const res = await postAPICall(
					instance,
					accounts[0],
					"/api/rfp-drafts",
					payload,
				);
				setCurrentDraftID(res.data.insertId);
				logDraftAutomation(
					true,
					`Draft "${draftName}" created (id ${res.data.insertId})`,
				);
			}
			lastSavedDraftSnapshotRef.current = JSON.stringify({
				draftName,
				...buildDraftFormFields(),
			});
			successMessageOpened(`Draft "${draftName}" saved.`);
			return true;
		} catch (error) {
			logDraftAutomation(
				false,
				`Error saving draft "${draftName}": ${error}`,
			);
			errorMessageOpened("Couldn't save this draft. Please try again.");
			return false;
		} finally {
			setIsSavingDraft(false);
		}
	};

	const saveAsDraftClicked = () => {
		if (!currentDraftID) {
			setDraftNameInput("");
			setSaveDraftDialogOpen(true);
		} else {
			saveDraft(currentDraftName);
		}
	};

	const saveDraftDialogConfirmed = () => {
		const trimmedName = draftNameInput.trim();
		if (!trimmedName) return;
		setSaveDraftDialogOpen(false);
		setCurrentDraftName(trimmedName);
		saveDraft(trimmedName);
	};

	const resetFormForNewRFP = () => {
		clearInputFields();
		setProposalSource("");
		setProposalSourceReferral("");
		setBuildingType("");
		setClaimCause({
			fire: false,
			flood: false,
			waterDamage: false,
			structural: false,
			design: false,
			cd: false,
		});
		setCurrentDraftID(null);
		setCurrentDraftName("");
		lastSavedDraftSnapshotRef.current = null;
		setDraftLoadVersion((v) => v + 1);
	};

	const newRFPClicked = () => {
		if (isDraftDirty) {
			setNewRFPConfirmOpen(true);
		} else {
			resetFormForNewRFP();
		}
	};

	const newRFPDiscardConfirmed = () => {
		setNewRFPConfirmOpen(false);
		resetFormForNewRFP();
	};

	const newRFPSaveConfirmed = async () => {
		const saved = await saveDraft(currentDraftName);
		if (saved) {
			setNewRFPConfirmOpen(false);
			resetFormForNewRFP();
		}
	};

	const loadDraftIntoForm = (draft) => {
		setCurrentDraftID(draft.id);
		setCurrentDraftName(draft.draft_name);

		if (draft.client_lookup_mode === "new") {
			createNewClientClicked();
		} else {
			searchExistingClientClicked(true);
		}
		setClientInfo({
			email: draft.client_email || "",
			firstname: draft.client_first_name || "",
			lastname: draft.client_last_name || "",
			recordID: draft.client_hubspot_id || "",
		});
		// createNewClientClicked/searchExistingClientClicked above both set
		// clientDetailedViewOpen themselves (true/false respectively, and
		// createNewClientClicked's early-return guard can skip it entirely),
		// so force it open here, after those calls, to guarantee the fields
		// are actually visible for a loaded draft.
		setClientDetailedViewOpen(true);

		if (draft.property_lookup_mode === "new") {
			createNewPropertyClicked();
		} else {
			searchExistingPropertyClicked(true);
		}
		setPropertyInfo({
			name: draft.property_name || "",
			address1: draft.property_address_line_1 || "",
			address2: draft.property_address_line_2 || "",
			city: draft.property_city || "",
			state: draft.property_state || "",
			zip: draft.property_zip || "",
			recordID: draft.property_hubspot_id || "",
		});
		setPropertyDetailedViewOpen(true);

		const formState = parseJSONField(draft.form_state, {});
		if (formState.serviceCheckboxes) {
			setServiceCheckboxes(formState.serviceCheckboxes);
		}
		if (formState.serviceCostBases) {
			setServiceCostBases(formState.serviceCostBases);
		}
		if (formState.componentCheckboxes) {
			setComponentCheckboxes(formState.componentCheckboxes);
			setNumComponents(
				Object.values(formState.componentCheckboxes).filter(Boolean)
					.length,
			);
		}
		setServiceInvasiveNumberOfOpenings(
			formState.serviceInvasiveNumberOfOpenings || "",
		);

		setClaimType(draft.claim_type || "");
		setClaimCause(
			parseJSONField(draft.claim_cause, {
				fire: false,
				flood: false,
				waterDamage: false,
				structural: false,
				design: false,
				cd: false,
			}),
		);

		setProposalSource(draft.proposal_source || "");
		setProposalSourceReferral(draft.proposal_source_referral || "");
		setScopeBackground(draft.scope_background || "");
		setScopeBackgroundPrefill(null);
		setAdditionalNotes(draft.additional_notes || "");
		setBuildingType(draft.building_type || "");
		setOIC(draft.oic || "");
		setProposalDueDate(
			draft.proposal_due_date
				? dayjs(draft.proposal_due_date)
				: dayjs().add(3, "day"),
		);

		successMessageOpened(`Draft "${draft.draft_name}" loaded.`);
		setDraftLoadVersion((v) => v + 1);
	};

	// RENDER
	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<div className="RFP">
				{/* Desktop only: on phones the mobile top bar shows the title, and
				    drafts and the amendment link aren't offered */}
				{!isMobile && (
				<div className="rfpHeaderContainer">
					<div className="rfpHeader">RFP</div>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "1em",
						}}
					>
						{currentDraftID && (
							<>
								<Chip
									label={`Editing draft: ${currentDraftName}`}
									sx={{
										backgroundColor: "rgba(24, 76, 147, 0.12)",
										color: "#184C93",
									}}
								/>
								<Button
									variant="text"
									size="small"
									onClick={newRFPClicked}
								>
									New RFP
								</Button>
							</>
						)}
						<Button
							variant="outlined"
							size="small"
							onClick={() => setDraftsDrawerOpen(true)}
						>
							My Drafts
						</Button>
						<div className="rfaLink">
							Need an amendment?{" "}
							<a href={`${process.env.REACT_APP_BASE_URL}/rfa`}>
								Click here.
							</a>
						</div>
					</div>
				</div>
				)}
				<div className="rfpCardDialogRow" id="successDialogDiv">
					<Collapse in={isSuccess} id="dialogCollapse">
						<Alert
							sx={{ marginTop: "1em", marginBottom: "1em" }}
							severity="success"
							onClose={successMessageClosed}
						>
							{successMessage}
						</Alert>
					</Collapse>
				</div>
				{validationIssues.length > 0 && (
					<div className="rfpCardDialogRow">
						<Alert
							severity="error"
							onClose={validationSummaryClosed}
							sx={{ mt: 1, mb: 1, boxSizing: "border-box" }}
						>
							<AlertTitle>
								Please fix these before submitting
							</AlertTitle>
							<List dense disablePadding>
								{validationIssues.map((issue) => (
									<ListItem
										key={issue.fieldName}
										disablePadding
									>
										<ListItemButton
											dense
											onClick={() =>
												focusValidationField(
													issue.fieldName,
												)
											}
										>
											<ListItemText
												primary={issue.message}
											/>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Alert>
					</div>
				)}
				<div className="rfpCard" id="clientInformationDiv">
					<div className="rfpCardHeader">
						<h2>
							Client Information
							{/* <IconButton
                                onClick={clientInformationHelpDialogOpened} 
                                size='small'
                            >
                                <HelpIcon fontSize='small' />
                            </IconButton> */}
						</h2>
						<ButtonGroup>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={
									createNewClientEnabled
										? "contained"
										: "outlined"
								}
								onClick={createNewClientClicked}
								disabled={createNewClientEnabled}
							>
								Create New
							</Button>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={
									searchExistingClientEnabled
										? "contained"
										: "outlined"
								}
								onClick={searchExistingClientClicked}
								disabled={searchExistingClientEnabled}
							>
								Search Existing
							</Button>
						</ButtonGroup>
					</div>
					<Collapse in={clientSearchBarOpen} id="searchBarCollapse">
						<div
							className="searchByRFPCardRow"
							id="clientSearchByFields"
						>
							<Autocomplete
								sx={{ flexGrow: 1 }}
								options={clientOptions}
								ref={clientSearchByValueRef}
								key={clientSearchByValueKey}
								getOptionLabel={(option) =>
									option.properties
										? `${
												option.properties.firstname ||
												""
											} ${
												option.properties.lastname || ""
											} (${option.properties.email || ""})`
										: option
								}
								filterSelectedOptions
								onChange={clientSearchByValueSelected}
								clearOnBlur={false}
								renderInput={(params) => (
									<TextField
										{...params}
										label={searchByOptionsMap.get(
											clientSearchByParameter,
										)}
										placeholder={searchByOptionsMap.get(
											clientSearchByParameter,
										)}
										value={clientSearchByValue || ""}
										onKeyUp={clientSearchByValueChanged}
									/>
								)}
							/>
							<FormControl id="searchByFormControl">
								<InputLabel id="clientSearchByLabel">
									Search By
								</InputLabel>
								<Select
									labelId="clientSearchByLabel"
									value={clientSearchByParameter}
									label="Search By"
									onChange={clientSearchByParameterSelected}
								>
									<MenuItem value="lastname">
										Last Name
									</MenuItem>
									<MenuItem value="email">Email</MenuItem>
								</Select>
							</FormControl>
						</div>
					</Collapse>
					<Collapse
						in={clientDetailedViewOpen}
						id="detailedViewCollapse"
					>
						<div className="rfpCardRow">
							<TextField
								id="rfpClientEmail"
								sx={{ flexGrow: 1 }}
								disabled={!createNewClientEnabled}
								name="email"
								error={
									clientEmailError ||
									Boolean(fieldErrors.clientEmail)
								}
								onBlur={checkEmail}
								onChange={clientInfoChanged}
								label="Email"
								value={clientInfo.email || ""}
								helperText={
									clientEmailError
										? "Contact with this email already exists."
										: fieldErrors.clientEmail || ""
								}
							/>
						</div>
						<div className="rfpCardRow">
							<TextField
								id="rfpClientFirstname"
								sx={{ flexGrow: 1 }}
								disabled={!createNewClientEnabled}
								name="firstname"
								onChange={clientInfoChanged}
								label="First Name"
								value={clientInfo.firstname || ""}
								error={Boolean(fieldErrors.clientFirstname)}
								helperText={fieldErrors.clientFirstname || ""}
							/>
							<TextField
								id="rfpClientLastname"
								sx={{ flexGrow: 1 }}
								disabled={!createNewClientEnabled}
								name="lastname"
								onChange={clientInfoChanged}
								label="Last Name"
								value={clientInfo.lastname || ""}
								error={Boolean(fieldErrors.clientLastname)}
								helperText={fieldErrors.clientLastname || ""}
							/>
						</div>
					</Collapse>
				</div>
				<div className="rfpCard" id="propertyInformationDiv">
					<div className="rfpCardHeader">
						<h2>
							Property Information
							{/* <IconButton
                                onClick={propertyInformationHelpDialogOpened}
                                size='small'
                            >
                                <HelpIcon fontSize='small' />
                            </IconButton> */}
						</h2>
						<ButtonGroup>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={
									createNewPropertyEnabled
										? "contained"
										: "outlined"
								}
								onClick={createNewPropertyClicked}
								disabled={createNewPropertyEnabled}
							>
								Create New
							</Button>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={
									searchExistingPropertyEnabled
										? "contained"
										: "outlined"
								}
								onClick={searchExistingPropertyClicked}
								disabled={searchExistingPropertyEnabled}
							>
								Search Existing
							</Button>
						</ButtonGroup>
					</div>
					<Collapse in={propertySearchBarOpen} id="searchBarCollapse">
						<div
							className="searchByRFPCardRow"
							id="rfpPropertySearchByFields"
						>
							<Autocomplete
								sx={{ flexGrow: 1 }}
								options={propertyOptions}
								ref={propertySearchByValueRef}
								getOptionLabel={(option) =>
									option.properties
										? option.properties.property_name
										: option
								}
								filterSelectedOptions
								onChange={propertySearchByValueSelected}
								clearOnBlur={false}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Property Name"
										placeholder="Property Name"
										value={propertySearchByValue || ""}
										onKeyUp={propertySearchByValueChanged}
									/>
								)}
							/>
						</div>
					</Collapse>
					<Collapse
						in={propertyDetailedViewOpen}
						id="detailedViewCollapse"
					>
						<div className="rfpCardRow">
							<TextField
								id="rfpPropertyName"
								sx={{ flexGrow: 1 }}
								disabled={!createNewPropertyEnabled}
								name="name"
								onChange={propertyInfoChanged}
								label="Property Name"
								value={propertyInfo.name || ""}
								error={Boolean(fieldErrors.propertyName)}
								helperText={fieldErrors.propertyName || ""}
							/>
						</div>
						<div className="rfpCardRow">
							<TextField
								id="rfpPropertyAddress1"
								sx={{ flexGrow: 1 }}
								disabled={!createNewPropertyEnabled}
								name="address1"
								onChange={propertyInfoChanged}
								label="Address Line 1"
								value={propertyInfo.address1 || ""}
								error={Boolean(fieldErrors.propertyAddress1)}
								helperText={fieldErrors.propertyAddress1 || ""}
							/>
							<TextField
								sx={{ flexGrow: 1 }}
								disabled={!createNewPropertyEnabled}
								name="address2"
								onChange={propertyInfoChanged}
								label="Address Line 2"
								value={propertyInfo.address2 || ""}
							/>
						</div>
						<div
							className="rfpCardRow"
							id="propertyColumnRFPCardRow"
						>
							<TextField
								id="rfpPropertyCity"
								sx={{ flexGrow: 1 }}
								disabled={!createNewPropertyEnabled}
								name="city"
								onChange={propertyInfoChanged}
								label="City"
								value={propertyInfo.city || ""}
								error={Boolean(fieldErrors.propertyCity)}
								helperText={fieldErrors.propertyCity || ""}
							/>
							<FormControl
								sx={{ flexGrow: 1 }}
								error={Boolean(fieldErrors.propertyState)}
							>
								<InputLabel id="propertyStateLabel">
									State
								</InputLabel>
								<Select
									id="rfpPropertyState"
									labelId="propertyStateLabel"
									value={propertyInfo.state}
									label="State"
									disabled={!createNewPropertyEnabled}
									name="state"
									onChange={propertyInfoChanged}
								>
									<MenuItem value="Washington">
										Washington
									</MenuItem>
									<MenuItem value="Oregon">Oregon</MenuItem>
									<MenuItem value="Utah">Utah</MenuItem>
									<MenuItem value="Idaho">Idaho</MenuItem>
								</Select>
								<FormHelperText>
									{fieldErrors.propertyState || ""}
								</FormHelperText>
							</FormControl>
							<TextField
								id="rfpPropertyZip"
								sx={{ flexGrow: 1 }}
								disabled={!createNewPropertyEnabled}
								name="zip"
								onChange={propertyInfoChanged}
								label="ZIP"
								value={propertyInfo.zip || ""}
								inputProps={{ inputMode: "numeric", maxLength: 9 }}
								error={Boolean(fieldErrors.propertyZip)}
								helperText={fieldErrors.propertyZip || ""}
							/>
						</div>
					</Collapse>
				</div>
				<div className="rfpCard" id="proposalSummaryDiv">
					<div className="rfpCardHeader">
						<h2>
							Proposal Summary
							{/* <IconButton
                                onClick={proposalSummaryHelpDialogOpened}
                                size='small'
                            >
                                <HelpIcon fontSize='small' />
                            </IconButton> */}
						</h2>
					</div>
					<div className="rfpCardRow" id="serviceComponentsRow">
						<div className="rfpCardGroup" id="serviceDiv">
							<h1 className="formGroupTitle">
								Service: What are we doing there? (select all
								that apply, T&C or Flat Fee options will appear
								below)
							</h1>
							<div
								className="rfpCardDialogRow"
								id="successDialogDiv"
							>
								<Collapse
									in={serviceMessageOpen}
									id="dialogCollapse"
								>
									<Alert
										sx={{
											marginTop: "1em",
											marginBottom: "1em",
										}}
										severity="info"
										onClose={serviceMessageClosed}
									>
										{serviceMessageText}
									</Alert>
								</Collapse>
							</div>
							{fieldErrors.services && (
								<div className="rfpFieldError">
									{fieldErrors.services}
								</div>
							)}
							<FormGroup id="formGroup">
								<FormControlLabel
									control={
										<Checkbox
											checked={serviceCheckboxes.visual}
											onChange={serviceVisualChanged}
											disabled={!enabledServices.visual}
										/>
									}
									label="Visual"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.visual}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="visual"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={serviceCheckboxes.invasive}
											onChange={serviceInvasiveChanged}
											disabled={!enabledServices.invasive}
										/>
									}
									label="Invasive"
								/>
								<Collapse
									id="serviceInvasiveCollapse"
									in={serviceCheckboxes.invasive}
								>
									<div id="serviceInvasiveCollapseContainer">
										<TextField
											sx={{ flexGrow: 1 }}
											label="# of Openings"
											value={
												serviceInvasiveNumberOfOpenings ||
												""
											}
											onChange={
												serviceInvasiveNumberOfOpeningsChanged
											}
										/>
									</div>
								</Collapse>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.invasive}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="invasive"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={serviceCheckboxes.pca}
											name="pca"
											onChange={genericServiceChanged}
											disabled={!enabledServices.pca}
										/>
									}
									label="PCA"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.pca}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="pca"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={serviceCheckboxes.design}
											name="design"
											onChange={genericServiceChanged}
											disabled={!enabledServices.design}
										/>
									}
									label="Design"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.design}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="design"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.bidAssistance
											}
											name="bidAssistance"
											onChange={genericServiceChanged}
											disabled={
												!enabledServices.bidAssistance
											}
										/>
									}
									label="Bid Assistance"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.bidAssistance}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="bidAssistance"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.contractAdministration
											}
											name="contractAdministration"
											onChange={genericServiceChanged}
											disabled={
												!enabledServices.contractAdministration
											}
										/>
									}
									label="Contract Administration"
								/>
								<Collapse
									id="costBasisCollapse"
									in={
										serviceCheckboxes.contractAdministration
									}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="contractAdministration"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
										<div className="costBasisContainerMessage">
											<mark>
												Please include fee structure
												(percentage, duration, estimate)
												preference in additional notes
												below
											</mark>
										</div>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.rapidVisual
											}
											onChange={serviceRapidVisualChanged}
											disabled={
												!enabledServices.rapidVisual
											}
										/>
									}
									label="Rapid Visual (Wellness Exam)"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.rapidVisual}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="rapidVisual"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.claimSupport
											}
											onChange={
												serviceClaimSupportChanged
											}
											disabled={
												!enabledServices.claimSupport
											}
										/>
									}
									label="Claim Support"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.claimSupport}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="claimSupport"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
									<div className="costBasisContainer">
										<div className="costBasisContainerMessage">
											Claim Type
										</div>
										<FormControl className="claimTypeOptions">
											<RadioGroup
												row
												id="claimTypeGroup"
												className="claimTypeGroup"
												name="claimType"
												value={claimType}
												onChange={claimTypeChanged}
											>
												<FormControlLabel
													value="COA"
													control={<Radio />}
													label="COA"
												/>
												<FormControlLabel
													value="Proposal"
													control={<Radio />}
													label="Proposal"
												/>
											</RadioGroup>
										</FormControl>
										{fieldErrors.claimType && (
											<div className="rfpFieldError">
												{fieldErrors.claimType}
											</div>
										)}
									</div>
									<div className="costBasisContainer">
										<div className="costBasisContainerMessage">
											Claim Cause
										</div>
										<FormControl className="claimTypeOptions">
											<FormGroup
												row
												id="claimCauseGroup"
												className="claimTypeGroup"
											>
												<FormControlLabel
													control={
														<Checkbox
															checked={claimCause.fire}
															name="fire"
															onChange={claimCauseChanged}
														/>
													}
													label="Fire"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={claimCause.flood}
															name="flood"
															onChange={claimCauseChanged}
														/>
													}
													label="Flood"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={
																claimCause.waterDamage
															}
															name="waterDamage"
															onChange={claimCauseChanged}
														/>
													}
													label="Water Damage"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={
																claimCause.structural
															}
															name="structural"
															onChange={claimCauseChanged}
														/>
													}
													label="Structural"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={claimCause.design}
															name="design"
															onChange={claimCauseChanged}
														/>
													}
													label="Design"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={claimCause.cd}
															name="cd"
															onChange={claimCauseChanged}
														/>
													}
													label="CD"
												/>
											</FormGroup>
										</FormControl>
										{fieldErrors.claimCause && (
											<div className="rfpFieldError">
												{fieldErrors.claimCause}
											</div>
										)}
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.feasibility
											}
											onChange={serviceFeasibilityChanged}
											disabled={
												!enabledServices.feasibility
											}
										/>
									}
									label="Feasibility"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.feasibility}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="feasibility"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.generalConsulting
											}
											onChange={
												serviceGeneralConsultingChanged
											}
											disabled={
												!enabledServices.generalConsulting
											}
										/>
									}
									label="General Consulting"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.generalConsulting}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="generalConsulting"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.engineeringStructural
											}
											onChange={
												serviceEngineeringStructuralChanged
											}
											disabled={
												!enabledServices.engineeringStructural
											}
										/>
									}
									label="Engineering - Structural Services"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.engineeringStructural}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="engineeringStructural"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={serviceCheckboxes.drainage}
											name="drainage"
											onChange={genericServiceChanged}
											disabled={!enabledServices.drainage}
										/>
									}
									label="Drainage"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.drainage}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="drainage"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.additionalServices
											}
											onChange={
												serviceAdditionalServicesChanged
											}
											disabled={
												!enabledServices.additionalServices
											}
										/>
									}
									label="Additional Services (NOT Amendment)"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.additionalServices}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="timeAndCost"
												id="formGroup"
												name="additionalServices"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.windowTesting
											}
											name="windowTesting"
											onChange={genericServiceChanged}
											disabled={
												!enabledServices.windowTesting
											}
										/>
									}
									label="Window Testing"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.windowTesting}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="windowTesting"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												serviceCheckboxes.romExercise
											}
											name="romExercise"
											onChange={genericServiceChanged}
											disabled={
												!enabledServices.romExercise
											}
										/>
									}
									label="ROM Exercise"
								/>
								<Collapse
									id="costBasisCollapse"
									in={serviceCheckboxes.romExercise}
								>
									<div className="costBasisContainer">
										<FormControl>
											<RadioGroup
												defaultValue="flatFee"
												id="formGroup"
												name="romExercise"
												onChange={costBasisChanged}
											>
												<FormControlLabel
													value="timeAndCost"
													control={<Radio />}
													label="Time & Cost"
												/>
												<FormControlLabel
													value="flatFee"
													control={<Radio />}
													label="Flat Fee"
												/>
											</RadioGroup>
										</FormControl>
									</div>
								</Collapse>
							</FormGroup>
						</div>
						<div className="rfpCardGroup" id="specificComponentDiv">
							<h1 className="formGroupTitle">
								Specific Component (select all that apply)
							</h1>
							<div
								className="rfpCardDialogRow"
								id="successDialogDiv"
							>
								<Collapse
									in={componentMessageOpen}
									id="dialogCollapse"
								>
									<Alert
										sx={{
											marginTop: "1em",
											marginBottom: "1em",
										}}
										severity="info"
										onClose={componentMessageClosed}
									>
										{componentMessageText}
									</Alert>
								</Collapse>
							</div>
							{fieldErrors.components && (
								<div className="rfpFieldError">
									{fieldErrors.components}
								</div>
							)}
							<FormGroup id="formGroup">
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.interiorUnit
											}
											name="interiorUnit"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.interiorUnit
											}
										/>
									}
									label="Interior - Unit"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.interiorAttic
											}
											name="interiorAttic"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.interiorAttic
											}
										/>
									}
									label="Interior - Attic"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.interiorCrawlSpace
											}
											name="interiorCrawlSpace"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.interiorCrawlSpace
											}
										/>
									}
									label="Interior - Crawl Space"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={componentCheckboxes.roof}
											name="roof"
											onChange={componentCheckboxChanged}
											disabled={!enabledComponents.roof}
										/>
									}
									label="Roof"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={componentCheckboxes.site}
											name="site"
											onChange={componentCheckboxChanged}
											disabled={!enabledComponents.site}
										/>
									}
									label="Site"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.carportGarage
											}
											name="carportGarage"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.carportGarage
											}
										/>
									}
									label="Carport / Garage"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={componentCheckboxes.decks}
											name="decks"
											onChange={componentCheckboxChanged}
											disabled={!enabledComponents.decks}
										/>
									}
									label="Decks / Walkways"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={componentCheckboxes.BE}
											name="BE"
											onChange={componentCheckboxChanged}
											disabled={!enabledComponents.BE}
										/>
									}
									label="Building Envelope (default)"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.windows
											}
											name="windows"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.windows
											}
										/>
									}
									label="Windows"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.stairway
											}
											name="stairway"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.stairway
											}
										/>
									}
									label="Stairway"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.plumbing
											}
											name="plumbing"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.plumbing
											}
										/>
									}
									label="Plumbing / Repipe"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={componentCheckboxes.HVAC}
											name="HVAC"
											onChange={componentCheckboxChanged}
											disabled={!enabledComponents.HVAC}
										/>
									}
									label="HVAC"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												componentCheckboxes.foundation
											}
											name="foundation"
											onChange={componentCheckboxChanged}
											disabled={
												!enabledComponents.foundation
											}
										/>
									}
									label="Foundation"
								/>
								{serviceCheckboxes.claimSupport && (
									<FormControlLabel
										control={
											<Checkbox
												checked={
													componentCheckboxes.other
												}
												name="other"
												onChange={
													componentCheckboxChanged
												}
												disabled={
													!enabledComponents.other
												}
											/>
										}
										label="Other"
									/>
								)}
							</FormGroup>
						</div>
					</div>
					<div className="rfpCardRow" id="proposalSourceDiv">
						<h1 className="proposalSourceTitle">
							RFP Source: Where did this proposal come from?
						</h1>
						<FormControl
							id="rfpProposalSource"
							sx={{ flexGrow: 1 }}
							error={Boolean(fieldErrors.proposalSource)}
						>
							<InputLabel id="proposalSourceLabel">
								Proposal Source
							</InputLabel>
							<Select
								labelId="proposalSourceLabel"
								value={proposalSource}
								label="Proposal Source"
								onChange={proposalSourceChanged}
							>
								{Object.keys(rfpSourceMap).map((option) => (
									<MenuItem value={option} key={option}>
										{rfpSourceMap[option]}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								{fieldErrors.proposalSource || ""}
							</FormHelperText>
						</FormControl>
						<Collapse
							id="proposalSourceCollapse"
							in={proposalSource === "referral"}
						>
							<div className="proposalSourceCollapseContainer">
								<TextField
									id="rfpProposalSourceReferral"
									sx={{ flexGrow: 1 }}
									name="proposalSourceReferral"
									onChange={proposalSourceReferralChanged}
									label="Referral Source (Who referred us?)"
									value={proposalSourceReferral || ""}
									error={Boolean(
										fieldErrors.proposalSourceReferral,
									)}
									helperText={
										fieldErrors.proposalSourceReferral || ""
									}
								/>
							</div>
						</Collapse>
					</div>
					<div className="rfpCardRow" id="rfpScopeBackground">
						<J2MultilineTextField
							label="Scope / Background"
							onChange={scopeBackgroundChanged}
							placeholder="Scope / Background (Proposal and building information)"
							value={scopeBackground || ""}
							rows={3}
						/>
					</div>
					{fieldErrors.scopeBackground && (
						<div className="rfpFieldError">
							{fieldErrors.scopeBackground}
						</div>
					)}
					{scopeBackgroundPrefill &&
						scopeBackgroundPrefill.recordID === propertyInfo.recordID &&
						(scopeBackground || "").startsWith(
							scopeBackgroundPrefill.description,
						) && (
							<div className="scopeHelperText">
								Building description filled in from this property's{" "}
								{dayjs(scopeBackgroundPrefill.submittedTime).format(
									"MMM D, YYYY",
								)}{" "}
								proposal. Review it before submitting.
							</div>
						)}
					{serviceCheckboxes.claimSupport && (
						<div className="scopeHelperText">
							Claim Type and Claim Cause will be appended to Scope /
							Background on submit.
						</div>
					)}
					<div className="rfpCardRow" id="additionalNotesDiv">
						<J2MultilineTextField
							label="Additional Notes"
							onChange={additionalNotesChanged}
							placeholder="Additional Notes (Budget information, unique requests, etc.)"
							value={additionalNotes || ""}
							rows={2}
						/>
					</div>
					<div className="rfpCardRow" id="bottomRFPCardRow">
						<FormControl
							id="rfpBuildingType"
							sx={{ flexGrow: 1 }}
							error={Boolean(fieldErrors.buildingType)}
						>
							<InputLabel id="buildingTypeLabel">
								Building Type
							</InputLabel>
							<Select
								labelId="buildingTypeLabel"
								value={buildingType}
								label="Building Type"
								onChange={buildingTypeChanged}
							>
								{Object.keys(buildingTypes).map((option) => (
									<MenuItem value={option} key={option}>
										{buildingTypes[option]}
									</MenuItem>
								))}
								{/* {buildingTypeMap
									.keys()
									.toArray()
									.map((option) => (
										<MenuItem value={option} key={option}>
											{buildingTypeMap.get(option)}
										</MenuItem>
									))} */}
							</Select>
							<FormHelperText>
								{fieldErrors.buildingType || ""}
							</FormHelperText>
						</FormControl>
						<FormControl
							id="rfpOIC"
							sx={{ flexGrow: 1 }}
							error={Boolean(fieldErrors.oic)}
						>
							<InputLabel id="oicLabel">OIC</InputLabel>
							<Select
								labelId="oicLabel"
								value={oic}
								label="OIC"
								onChange={oicChanged}
							>
								<MenuItem value="WA">WA</MenuItem>
								<MenuItem value="OR">OR</MenuItem>
								<MenuItem value="UT">UT</MenuItem>
							</Select>
							<FormHelperText>
								{fieldErrors.oic || ""}
							</FormHelperText>
						</FormControl>
						<DatePicker
							label="Proposal Due Date"
							value={proposalDueDate}
							onChange={proposalDueDateChanged}
							slotProps={{
								textField: {
									id: "rfpProposalDueDate",
									size: "medium",
									error: Boolean(fieldErrors.proposalDueDate),
									helperText:
										fieldErrors.proposalDueDate || "",
								},
							}}
						/>
					</div>
					<J2ErrorDialog
						open={isError}
						onClose={errorMessageClosed}
						errorMessage={errorMessage}
					/>
				</div>
				<div className="rfpButtonContainer">
					{!isMobile && (
					<Button
						color="primary"
						variant="outlined"
						onClick={saveAsDraftClicked}
						disabled={isSavingDraft || isSubmitting}
					>
						{currentDraftID ? "Update Draft" : "Save as Draft"}
					</Button>
					)}
					<div className="rfpSubmitButtonContainer">
						<Button
							color="primary"
							variant="contained"
							onClick={submitRFPFormPressed}
							disabled={isSubmitting}
						>
							Submit
						</Button>
						{isSubmitting && (
							<CircularProgress
								size={24}
								sx={{
									color: "primary",
									position: "absolute",
									top: "50%",
									left: "50%",
									marginTop: "-12px",
									marginLeft: "-12px",
								}}
							/>
						)}
					</div>
				</div>
			</div>
			<ClientInformationHelpDialog
				open={clientInformationHelpDialogOpen}
				onClose={clientInformationHelpDialogClosed}
			/>
			<PropertyInformationHelpDialog
				open={propertyInformationHelpDialogOpen}
				onClose={propertyInformationHelpDialogClosed}
			/>
			<ProposalSummaryHelpDialog
				open={proposalSummaryHelpDialogOpen}
				onClose={proposalSummaryHelpDialogClosed}
			/>
			<Dialog
				open={saveDraftDialogOpen}
				onClose={() => setSaveDraftDialogOpen(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle id="rfpSaveDraftDialogTitle">
					Name this draft
				</DialogTitle>
				<DialogContent id="rfpSaveDraftDialogContent">
					<TextField
						autoFocus
						fullWidth
						label="Draft Name"
						value={draftNameInput}
						onChange={(event) => setDraftNameInput(event.target.value)}
						sx={{ marginTop: "0.5em" }}
					/>
					<div className="rfpDialogButtons">
						<Button
							variant="outlined"
							color="error"
							onClick={() => setSaveDraftDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							disabled={!draftNameInput.trim()}
							onClick={saveDraftDialogConfirmed}
						>
							Save
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<Dialog
				open={newRFPConfirmOpen}
				onClose={() => setNewRFPConfirmOpen(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle id="rfpNewRFPConfirmDialogTitle">
					Save changes before starting a new RFP?
				</DialogTitle>
				<DialogContent id="rfpNewRFPConfirmDialogContent">
					<Typography variant="body2">
						You're currently editing the draft "{currentDraftName}".
						Save your changes to it, or discard them and start a new
						RFP?
					</Typography>
					<div className="rfpDialogButtons">
						<Button
							variant="text"
							onClick={() => setNewRFPConfirmOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="outlined"
							color="error"
							onClick={newRFPDiscardConfirmed}
						>
							Discard
						</Button>
						<Button
							variant="contained"
							onClick={newRFPSaveConfirmed}
							disabled={isSavingDraft}
						>
							Save
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<RFPDraftsDrawer
				open={!isMobile && draftsDrawerOpen}
				onClose={() => setDraftsDrawerOpen(false)}
				instance={instance}
				account={accounts[0]}
				employeeID={employeeID}
				onOpenDraft={loadDraftIntoForm}
				logAutomation={logDraftAutomation}
			/>
		</LocalizationProvider>
	);
};

export default RFP;
